/**
 * What a trouble code means, for any car.
 *
 * Three layers, each filling in what the one before could not:
 *
 *   1. The code's structure, fixed by SAE J2012 for every make: the letter
 *      names the system, the first digit says whether the meaning is generic
 *      or the carmaker's own, and for powertrain codes the next digit names
 *      the subsystem.
 *   2. Families of related generic codes -- misfire in cylinder N, oxygen
 *      sensor B-S, lost communication with module X -- each with what it
 *      means, whether it is safe to drive on, what the driver may notice,
 *      likely causes and what to check.
 *   3. The fault type in the code's name ("circuit low", "range/performance",
 *      "intermittent"), which says a lot on its own about where to look.
 *
 * Carmaker-defined codes cannot be named without the maker's own tables, and
 * the explanation says so rather than guessing.
 */
import type { FuelType } from '../types.js';
import type { DtcStatus } from './dtc.js';

export type DtcSeverity = 'stop' | 'soon' | 'check' | 'info';

export interface DtcContext {
  /** Tailors likely causes, e.g. spark plugs against glow plugs. */
  fuelType?: FuelType;
  /** From the VIN, to say whose documentation a carmaker code is in. */
  manufacturer?: string;
}

export interface DtcExplanation {
  code: string;
  title: string;
  /** False when no name is on file and the title only states the area. */
  titleKnown: boolean;
  /** Powertrain, Chassis, Body or Network. */
  system: string;
  /** The subsystem, as far as the code's structure says. */
  area: string;
  /** Who defines the meaning: the OBD-II standard, or the carmaker. */
  definedBy: 'generic' | 'manufacturer';
  /** What the code means, in plain words. */
  summary: string;
  /** What the kind of fault in the name usually points to. */
  faultType?: string;
  severity: DtcSeverity;
  /** Whether and how to keep driving. */
  drive: string;
  /** What the driver may notice, and what the fault can lead to. */
  effects: string[];
  /** Most likely first. */
  causes: string[];
  /** What to look at, in a sensible order. */
  checks: string[];
  cylinder?: number;
  bank?: number;
  sensor?: number;
  module?: string;
}

export const SEVERITY_LABELS: Record<DtcSeverity, string> = {
  stop: 'Stop safely',
  soon: 'Fix soon',
  check: 'Check when convenient',
  info: 'For information',
};

export const SEVERITY_RANK: Record<DtcSeverity, number> = { stop: 3, soon: 2, check: 1, info: 0 };

const DEFAULT_DRIVE: Record<DtcSeverity, string> = {
  stop: 'Stop as soon as it is safe and switch the engine off. Carrying on risks serious damage or is unsafe.',
  soon: 'The car can usually be driven gently for now, but have it looked at soon: the fault can get worse or cause other damage.',
  check: 'Usually safe to keep driving. Have it checked when convenient: it can affect economy, emissions or an inspection.',
  info: 'No immediate action is needed.',
};

export const STATUS_EXPLANATIONS: Record<DtcStatus, string> = {
  stored:
    'Confirmed: the fault was detected on more than one drive, and it normally lights the engine warning lamp. '
    + 'It stays in memory until it is cleared or the car sees it fixed for a number of drives.',
  pending:
    'Seen once and not yet confirmed. If the fault does not come back over the next drives, the car drops it by itself. '
    + 'A pending code is the early warning: worth watching even if no lamp is on.',
  permanent:
    'Only the car can erase this one, after its own self-test passes following a repair. Clearing codes with a tool '
    + 'does not remove it, and some emissions inspections check for it.',
};

// ---------------------------------------------------------------- structure

export interface DtcStructure {
  valid: boolean;
  system: string;
  definedBy: 'generic' | 'manufacturer';
  area: string;
}

const SYSTEMS: Record<string, string> = { P: 'Powertrain', C: 'Chassis', B: 'Body', U: 'Network' };

const P0_AREAS: Record<string, string> = {
  '0': 'Fuel and air metering, auxiliary emission controls',
  '1': 'Fuel and air metering',
  '2': 'Fuel and air metering (injector circuit)',
  '3': 'Ignition system or misfire',
  '4': 'Auxiliary emission controls',
  '5': 'Vehicle speed, idle control and auxiliary inputs',
  '6': 'Control module and output circuits',
  '7': 'Transmission',
  '8': 'Transmission',
  '9': 'Transmission',
  A: 'Hybrid or electric propulsion',
  B: 'Hybrid or electric propulsion',
  C: 'Hybrid or electric propulsion',
  D: 'Hybrid or electric propulsion',
  E: 'Hybrid or electric propulsion',
  F: 'Hybrid or electric propulsion',
};

const P2_AREAS: Record<string, string> = {
  '0': 'Fuel and air metering, auxiliary emission controls',
  '1': 'Fuel and air metering, auxiliary emission controls',
  '2': 'Fuel and air metering, auxiliary emission controls',
  '3': 'Ignition system or misfire',
  '4': 'Auxiliary emission controls',
  '5': 'Auxiliary inputs',
  '6': 'Control module and output circuits',
  '7': 'Transmission',
  '8': 'Transmission',
  '9': 'Fuel and air metering, auxiliary emission controls',
  A: 'Fuel and air metering, auxiliary emission controls',
};

const U_AREAS: Record<string, string> = {
  '0': 'Network wiring (the data bus itself)',
  '1': 'Network communication (a module stopped answering)',
  '2': 'Network communication (a module stopped answering)',
  '3': 'Network software (module configuration or compatibility)',
  '4': 'Network data (a module sent invalid data)',
};

export function dtcStructure(code: string): DtcStructure {
  const valid = /^[PCBU][0-3][0-9A-F]{3}$/.test(code);
  const letter = code[0] ?? 'P';
  const d1 = code[1] ?? '0';
  const d2 = code[2] ?? '0';
  const system = SYSTEMS[letter] ?? 'Unknown';

  let definedBy: 'generic' | 'manufacturer';
  if (letter === 'P') {
    definedBy = d1 === '1' || (d1 === '3' && d2 <= '3') ? 'manufacturer' : 'generic';
  } else {
    definedBy = d1 === '1' || d1 === '2' ? 'manufacturer' : 'generic';
  }

  let area: string;
  if (letter === 'P') {
    if (d1 === '0') area = P0_AREAS[d2] ?? 'Powertrain';
    else if (d1 === '2') area = P2_AREAS[d2] ?? 'Powertrain (reserved range)';
    else if (d1 === '3') area = d2 === '4' ? 'Cylinder deactivation' : d2 <= '3' ? 'Powertrain' : 'Powertrain (reserved range)';
    // Carmakers mostly follow the generic grouping in their own range, but are not required to.
    else area = P0_AREAS[d2] ? P0_AREAS[d2] + ' (usually)' : 'Powertrain';
  } else if (letter === 'U') {
    area = definedBy === 'generic' ? U_AREAS[d2] ?? 'Vehicle network' : 'Vehicle network';
  } else if (letter === 'B') {
    area = definedBy === 'generic' && d2 === '0'
      ? 'Restraints (airbags and seat-belt pretensioners)'
      : 'Body: airbags, climate, lighting, locks and comfort systems';
  } else {
    area = 'Chassis: brakes, steering, suspension, traction and stability control';
  }

  return { valid, system, definedBy, area };
}

// -------------------------------------------------------------- explanation

interface Knowledge {
  title?: string;
  summary: string;
  severity: DtcSeverity;
  drive?: string;
  effects: string[];
  causes: string[];
  checks: string[];
  cylinder?: number;
  bank?: number;
  sensor?: number;
  module?: string;
}

interface Parsed {
  code: string;
  letter: string;
  /** The four digits after the letter. */
  digits: string;
  /** Those digits read as a decimal number, when they are all decimal. */
  n: number;
  ctx: DtcContext;
}

export function explainDtc(code: string, ctx: DtcContext = {}): DtcExplanation {
  const upper = code.trim().toUpperCase();
  const s = dtcStructure(upper);
  const digits = upper.slice(1);
  const parsed: Parsed = {
    code: upper,
    letter: upper[0] ?? 'P',
    digits,
    n: /^\d{4}$/.test(digits) ? parseInt(digits, 10) : NaN,
    ctx,
  };

  const k: Knowledge | undefined = s.valid && s.definedBy === 'generic' ? findFamily(parsed) : undefined;
  const known = k?.title ?? TITLES[upper];
  const base = k ?? fallback(parsed, s);

  const title = known ?? (s.definedBy === 'manufacturer' ? 'Carmaker-defined code' : s.area);
  const faultType = known ? faultTypeOf(known) : undefined;

  const out: DtcExplanation = {
    code: upper,
    title,
    titleKnown: known !== undefined,
    system: s.system,
    area: s.area,
    definedBy: s.definedBy,
    summary: base.summary,
    severity: base.severity,
    drive: base.drive ?? DEFAULT_DRIVE[base.severity],
    effects: base.effects,
    causes: base.causes,
    checks: base.checks,
  };
  if (faultType) out.faultType = faultType;
  if (base.cylinder !== undefined) out.cylinder = base.cylinder;
  if (base.bank !== undefined) out.bank = base.bank;
  if (base.sensor !== undefined) out.sensor = base.sensor;
  if (base.module !== undefined) out.module = base.module;
  if (!s.valid) {
    out.summary = 'This does not look like a standard trouble code. It may have been misread from the car.';
    out.severity = 'info';
    out.drive = DEFAULT_DRIVE.info;
  }
  return out;
}

/** Name only: the known title, or undefined when there is none on file. */
export function dtcTitle(code: string): string | undefined {
  const upper = code.trim().toUpperCase();
  const s = dtcStructure(upper);
  if (!s.valid || s.definedBy !== 'generic') return undefined;
  const digits = upper.slice(1);
  const family = findFamily({
    code: upper, letter: upper[0] ?? 'P', digits,
    n: /^\d{4}$/.test(digits) ? parseInt(digits, 10) : NaN, ctx: {},
  });
  return family?.title ?? TITLES[upper];
}

// ---------------------------------------------------------------- fault type

const FAULT_TYPES: { pattern: RegExp; text: string }[] = [
  {
    pattern: /intermittent|erratic/i,
    text: 'The signal dropped out or jumped briefly. That usually means a loose or corroded connector or a chafed wire, '
      + 'and the fault may not be present right now.',
  },
  {
    pattern: /too low|too high|pressure (low|high)|lean|rich|insufficient|excessive|over ?temperature|overboost|underboost|overspeed|leak|deteriorated|slipping|efficiency below|level too/i,
    text: 'The measured condition itself is out of limits, rather than a sensor or wiring fault: the reading is probably right.',
  },
  {
    pattern: /\b(circuit|sensor|switch|solenoid|valve)\b.*\blow\b|\blow (input|voltage)\b/i,
    text: 'The signal is below anything the part can normally produce: usually a short to ground or a broken wire '
      + '(open circuit), sometimes a failed sensor.',
  },
  {
    pattern: /\b(circuit|sensor|switch|solenoid|valve)\b.*\bhigh\b|\bhigh (input|voltage)\b/i,
    text: 'The signal is above the normal range: usually a short to a supply voltage or a lost ground connection, '
      + 'sometimes a failed sensor.',
  },
  {
    pattern: /range\/performance|performance|rationality|slow response/i,
    text: 'The signal is electrically fine but does not make sense next to other readings. Often a sensor ageing or drifting '
      + 'out of calibration, or the measured thing itself really being wrong (a leak, a blockage, a sticking part).',
  },
  {
    pattern: /correlation/i,
    text: 'Two readings that should agree do not. One of the two sensors, their wiring, or the mechanical link between '
      + 'them is at fault.',
  },
  {
    pattern: /stuck/i,
    text: 'A part is not moving, or a reading is not changing, when it should.',
  },
  {
    pattern: /not learned|programming|memory|check ?sum|processor|software|internal/i,
    text: 'A control module or its stored settings. Often needs a workshop tool to relearn or reprogram.',
  },
  {
    pattern: /circuit|open|shorted|no signal|no activity/i,
    text: 'An electrical fault in the part or its wiring: a broken wire, a short, a corroded connector or the part itself.',
  },
];

function faultTypeOf(title: string): string | undefined {
  return FAULT_TYPES.find((f) => f.pattern.test(title))?.text;
}

// ------------------------------------------------------------------ helpers

const isPetrol = (ctx: DtcContext): boolean =>
  ctx.fuelType === 'petrol' || ctx.fuelType === 'lpg' || ctx.fuelType === 'cng' || ctx.fuelType === 'ethanol'
  || ctx.fuelType === 'hybrid';
const isDiesel = (ctx: DtcContext): boolean => ctx.fuelType === 'diesel';

/** Causes that depend on the engine type, with both lists when it is not known. */
function byFuel(ctx: DtcContext, petrol: string[], diesel: string[]): string[] {
  if (isPetrol(ctx)) return petrol;
  if (isDiesel(ctx)) return diesel;
  return [...petrol.map((c) => 'Petrol: ' + lowerFirst(c)), ...diesel.map((c) => 'Diesel: ' + lowerFirst(c))];
}

const lowerFirst = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);
const inRange = (n: number, lo: number, hi: number): boolean => n >= lo && n <= hi;

const SIGNALS_TIP = 'Watch the related live reading in the Signals tab while the fault is present.';
const CONNECTOR_CHECK = 'Inspect the connector and wiring of the part named in the code for corrosion, damage or a loose fit.';
const CLEAR_AND_RETEST =
  'After fixing, clear the code and drive normally for a few days: if it does not come back, the fix worked.';

/** Suffixes of the classic five-code sensor groups: circuit, range, low, high, intermittent. */
const SENSOR_SUFFIX = ['Circuit', 'Circuit Range/Performance', 'Circuit Low', 'Circuit High', 'Circuit Intermittent'];

function sensorTitle(name: string, offset: number): string {
  return name + ' ' + (SENSOR_SUFFIX[offset] ?? 'Circuit');
}

// ----------------------------------------------------------------- families

type Family = (p: Parsed) => Knowledge | undefined;

function findFamily(p: Parsed): Knowledge | undefined {
  const table = p.letter === 'P' ? POWERTRAIN : p.letter === 'U' ? NETWORK : p.letter === 'B' ? BODY : CHASSIS;
  for (const family of table) {
    const k = family(p);
    if (k) return k;
  }
  return undefined;
}

// ...................................................................... P0xxx

const vvt: Family = ({ n }) => {
  if (!inRange(n, 10, 25) || inRange(n, 16, 19)) return undefined;
  const bank = n <= 15 ? 1 : 2;
  const offset = (n - (bank === 1 ? 10 : 20)) % 3;
  const cam = n - (bank === 1 ? 10 : 20) < 3 ? 'A' : 'B';
  const title = '"' + cam + '" Camshaft Position '
    + (offset === 0 ? 'Actuator Circuit' : offset === 1 ? '- Timing Over-Advanced or System Performance' : '- Timing Over-Retarded')
    + ' (Bank ' + bank + ')';
  return {
    title,
    bank,
    summary: 'Variable valve timing: the engine computer turns the camshaft to adjust when the valves open, and here the '
      + (offset === 0 ? 'circuit that controls it has an electrical fault.' : 'camshaft is not where it was told to be.'),
    severity: 'soon',
    effects: ['Rough idle or stalling', 'Less power and higher fuel consumption', 'A rattle at start-up on some engines'],
    causes: [
      'Low, dirty or wrong-grade engine oil: the adjuster is moved by oil pressure',
      'Oil control solenoid for that camshaft',
      'Wiring or connector to the solenoid',
      'Worn camshaft adjuster (phaser) or stretched timing chain',
    ],
    checks: [
      'Check the oil level and when it was last changed',
      CONNECTOR_CHECK,
      'If the engine rattles at start-up, have the timing chain and adjuster inspected',
    ],
  };
};

const crankCamCorrelation: Family = ({ n }) => {
  if (!inRange(n, 16, 19)) return undefined;
  const bank = n <= 17 ? 1 : 2;
  const sensor = n % 2 === 0 ? 'A' : 'B';
  return {
    title: 'Crankshaft Position - Camshaft Position Correlation (Bank ' + bank + ' Sensor ' + sensor + ')',
    bank,
    summary: 'The crankshaft and camshaft sensors disagree about where the engine is in its cycle: the timing between '
      + 'the two shafts is off, or one of the signals is wrong.',
    severity: 'soon',
    drive: 'Drive gently and have it checked promptly. If the engine rattles, runs very roughly or loses power, stop: '
      + 'a stretched or slipped timing chain or belt can destroy the engine.',
    effects: ['Hard starting or stalling', 'Rough running and less power'],
    causes: [
      'Stretched timing chain, or a timing belt that has jumped a tooth',
      'Faulty camshaft or crankshaft position sensor',
      'Camshaft adjuster stuck, often from low or dirty oil',
      'Damaged sensor target wheel',
    ],
    checks: ['Check the oil level and condition', CONNECTOR_CHECK, 'Have the timing checked by a workshop'],
  };
};

const o2Heater: Family = ({ n }) => {
  const bases: Record<number, [number, number]> = { 30: [1, 1], 36: [1, 2], 42: [1, 3], 50: [2, 1], 56: [2, 2], 62: [2, 3] };
  const base = Object.keys(bases).map(Number).find((b) => inRange(n, b, b + 2));
  if (base === undefined) return undefined;
  const [bank, sensor] = bases[base] as [number, number];
  const suffix = ['Control Circuit', 'Control Circuit Low', 'Control Circuit High'][n - base];
  return {
    title: 'O2 Sensor Heater ' + suffix + ' (Bank ' + bank + ' Sensor ' + sensor + ')',
    bank,
    sensor,
    summary: 'The oxygen sensor has a small heater so it works soon after a cold start. Its circuit has an electrical fault.',
    severity: 'check',
    effects: [
      'Slightly higher fuel consumption and emissions after cold starts',
      'The oxygen sensor self-test cannot complete, which fails an emissions inspection',
    ],
    causes: ['Failed heater inside the oxygen sensor (the usual answer)', 'Blown fuse for the sensor heaters', 'Wiring or connector near the hot exhaust'],
    checks: ['Check the fuse for the oxygen sensor heaters', CONNECTOR_CHECK, 'Replacing the sensor is the common fix'],
  };
};

const turboControl: Family = ({ n }) => {
  if (inRange(n, 33, 35)) {
    return {
      title: 'Turbocharger Bypass Valve Control Circuit' + ['', ' Low', ' High'][n - 33],
      summary: 'The valve that releases boost when the throttle closes has an electrical fault in its control circuit.',
      severity: 'check',
      effects: ['A whoosh or flutter when lifting off', 'Boost may be reduced'],
      causes: ['Bypass valve or its solenoid', 'Wiring or connector'],
      checks: [CONNECTOR_CHECK],
    };
  }
  if (inRange(n, 45, 49)) {
    const titles = [
      'Turbocharger Boost Control "A" Circuit/Open',
      'Turbocharger Boost Control "A" Circuit Range/Performance',
      'Turbocharger Boost Control "A" Circuit Low',
      'Turbocharger Boost Control "A" Circuit High',
      'Turbocharger Turbine Overspeed',
    ];
    return turboKnowledge(titles[n - 45] as string, n === 49);
  }
  return undefined;
};

function turboKnowledge(title: string, overspeed: boolean): Knowledge {
  return {
    title,
    summary: overspeed
      ? 'The turbocharger spun faster than it is designed to.'
      : 'The system that controls how much boost the turbocharger makes has a fault.',
    severity: 'soon',
    drive: 'Drive gently. The engine usually limits power to protect itself. If you hear a siren-like whine, see blue '
      + 'or heavy smoke, or the engine revs on its own, stop: the turbo may be failing.',
    effects: ['Reduced power, often a sudden "limp mode"', 'Boost that comes and goes'],
    causes: [
      'Vacuum hose or actuator for the wastegate or variable vanes',
      'Boost control solenoid or its wiring',
      'Sticking variable-geometry vanes (common on diesels driven gently)',
      'Boost leak from a split hose or intercooler',
    ],
    checks: [
      'Look for split or disconnected hoses between the turbo, intercooler and engine',
      'Check the vacuum lines to the turbo actuator',
      SIGNALS_TIP,
    ],
  };
}

const fuelPressure: Family = ({ n, ctx }) => {
  if (!inRange(n, 87, 94)) return undefined;
  const titles: Record<number, string> = {
    87: 'Fuel Rail/System Pressure - Too Low',
    88: 'Fuel Rail/System Pressure - Too High',
    89: 'Fuel Pressure Regulator 1 Performance',
    90: 'Fuel Pressure Regulator 1 Control Circuit',
    91: 'Fuel Pressure Regulator 1 Control Circuit Low',
    92: 'Fuel Pressure Regulator 1 Control Circuit High',
    93: 'Fuel System Leak Detected - Large Leak',
    94: 'Fuel System Leak Detected - Small Leak',
  };
  if (n === 93 || n === 94) {
    return {
      title: titles[n],
      summary: 'The engine computer has seen fuel pressure fall in a way that suggests fuel is escaping from the high-pressure system.',
      severity: n === 93 ? 'stop' : 'soon',
      drive: n === 93
        ? 'Stop as soon as it is safe and switch off. A large fuel leak under pressure is a fire risk. Do not restart until it has been checked.'
        : 'Check for a fuel smell or wet patches before driving on, and have it looked at soon: a leak is a fire risk.',
      effects: ['Smell of fuel', 'Engine cutting out or refusing to start', 'The engine may shut down to protect itself'],
      causes: ['Leaking fuel line, union or injector seal', 'Pressure relief valve', 'Rail pressure sensor giving a false reading'],
      checks: ['Look and smell for fuel around the engine and under the car, engine off', 'Have the high-pressure system inspected'],
    };
  }
  return {
    title: titles[n],
    summary: n === 88
      ? 'Fuel pressure is higher than the engine computer asked for.'
      : n === 87
        ? 'Fuel pressure is lower than the engine computer asked for: the engine is not getting enough fuel.'
        : 'The part that regulates fuel pressure is not doing what it is told, or its circuit has a fault.',
    severity: 'soon',
    effects: ['Hesitation or loss of power under load', 'Hard starting or stalling', 'Limp mode'],
    causes: [
      ...byFuel(ctx, ['Weak fuel pump or clogged fuel filter'], ['Clogged fuel filter or air in the fuel', 'High-pressure pump or its metering valve']),
      'Pressure regulator or its wiring',
      'Rail pressure sensor giving a wrong reading',
      'Running very low on fuel',
    ],
    checks: [
      'Make sure there is enough fuel in the tank',
      'Check when the fuel filter was last changed',
      'A workshop can measure the actual fuel pressure against the reading',
    ],
  };
};

const ambientSensors: Family = ({ n }) => {
  if (n === 68) {
    return {
      title: 'MAP/MAF - Throttle Position Correlation',
      summary: 'The air the engine is taking in does not match how far the throttle is open.',
      severity: 'soon',
      effects: ['Reduced power or limp mode', 'Hesitation'],
      causes: ['Air leak between the air flow sensor and the engine', 'Dirty or faulty air flow sensor', 'Dirty throttle body'],
      checks: ['Look for loose or split intake hoses', 'Clean the throttle body', SIGNALS_TIP],
    };
  }
  if (n === 69) {
    return {
      title: 'Manifold Absolute Pressure - Barometric Pressure Correlation',
      summary: 'With the engine stopped the manifold and barometric pressures should be equal, and they are not.',
      severity: 'check',
      effects: ['Slightly wrong fuelling', 'Can affect boost control on turbocharged engines'],
      causes: ['Manifold pressure sensor or its hose', 'Barometric sensor (often inside the engine computer)'],
      checks: ['Check the vacuum hose to the manifold pressure sensor', SIGNALS_TIP],
    };
  }
  if (inRange(n, 70, 74)) {
    return {
      title: sensorTitle('Ambient Air Temperature Sensor', n - 70),
      summary: 'The outside temperature sensor reading is missing or implausible.',
      severity: 'info',
      effects: ['Wrong outside temperature display', 'Automatic climate control may behave oddly'],
      causes: ['Sensor behind the front bumper, often damaged by stones or water', 'Wiring or connector'],
      checks: [CONNECTOR_CHECK],
    };
  }
  return undefined;
};

/** The five-code sensor groups of P0100-P0124, P0180-P0199, P0220-P0229. */
const engineSensors: Family = ({ n, ctx }) => {
  const groups: { base: number; name: string; k: (offset: number) => Omit<Knowledge, 'title'> }[] = [
    {
      base: 100,
      name: 'Mass or Volume Air Flow',
      k: () => ({
        summary: 'The air flow sensor measures how much air the engine takes in, which sets how much fuel is injected. Its reading is missing or implausible.',
        severity: 'soon',
        effects: ['Hesitation, flat spots or stalling', 'Higher fuel consumption', ...(isPetrol(ctx) ? [] : ['Black smoke or limp mode on diesels'])],
        causes: [
          'Dirty or failed air flow sensor',
          'Air leak between the sensor and the engine',
          'Clogged air filter',
          'Wiring or connector',
        ],
        checks: [
          'Check the air filter and the hoses after the sensor',
          CONNECTOR_CHECK,
          'Compare the Mass Air Flow reading in the Signals tab at idle and when revving',
        ],
      }),
    },
    {
      base: 105,
      name: 'Manifold Absolute Pressure/Barometric Pressure',
      k: () => ({
        summary: 'The manifold pressure sensor tells the engine computer how hard the engine is working. Its reading is missing or implausible.',
        severity: 'soon',
        effects: ['Poor running and higher consumption', 'Wrong boost on turbocharged engines'],
        causes: ['Sensor or its vacuum hose', 'Air leak in the intake', 'Wiring or connector'],
        checks: ['Check the sensor\'s hose for splits', CONNECTOR_CHECK, SIGNALS_TIP],
      }),
    },
    {
      base: 110,
      name: 'Intake Air Temperature',
      k: () => ({
        summary: 'The intake air temperature reading is missing or implausible. The engine computer uses a fixed value instead.',
        severity: 'check',
        effects: ['Slightly worse economy or performance', 'Harder cold starts in some cars'],
        causes: ['Sensor (often built into the air flow sensor)', 'Wiring or connector'],
        checks: ['With a cold engine, the Intake Air Temperature reading should be close to the outside temperature', CONNECTOR_CHECK],
      }),
    },
    {
      base: 115,
      name: 'Engine Coolant Temperature',
      k: () => ({
        summary: 'The engine temperature reading is missing or implausible. The engine computer assumes a value, and the cooling fan often runs all the time as a precaution.',
        severity: 'soon',
        drive: 'Drive gently and watch the temperature gauge, which may itself be wrong. If there is steam, a sweet smell or the car overheats, stop.',
        effects: ['Temperature gauge wrong or stuck', 'Cooling fan running constantly', 'Hard starting and high consumption', 'Real overheating could go unnoticed'],
        causes: ['Coolant temperature sensor', 'Wiring or connector', 'Low coolant, leaving the sensor in air'],
        checks: ['Check the coolant level with the engine cold', CONNECTOR_CHECK, 'Compare the Engine Coolant Temperature reading with the gauge'],
      }),
    },
    {
      base: 120,
      name: 'Throttle/Pedal Position Sensor A',
      k: () => throttleKnowledge(),
    },
    {
      base: 180,
      name: 'Fuel Temperature Sensor A',
      k: () => ({
        summary: 'The fuel temperature reading is missing or implausible.',
        severity: 'check',
        effects: ['Slightly less accurate fuelling', 'Possible hard hot starts'],
        causes: ['Sensor', 'Wiring or connector'],
        checks: [CONNECTOR_CHECK],
      }),
    },
    {
      base: 185,
      name: 'Fuel Temperature Sensor B',
      k: () => ({
        summary: 'The second fuel temperature reading is missing or implausible.',
        severity: 'check',
        effects: ['Slightly less accurate fuelling'],
        causes: ['Sensor', 'Wiring or connector'],
        checks: [CONNECTOR_CHECK],
      }),
    },
    {
      base: 190,
      name: 'Fuel Rail Pressure Sensor',
      k: () => ({
        summary: 'The fuel rail pressure reading is missing or implausible, so fuel pressure cannot be controlled properly.',
        severity: 'soon',
        effects: ['Hard starting, stalling or limp mode', 'Loss of power'],
        causes: ['Rail pressure sensor', 'Wiring or connector', 'A real fuel pressure problem'],
        checks: [CONNECTOR_CHECK, 'A workshop can compare the reading with a pressure gauge'],
      }),
    },
    {
      base: 195,
      name: 'Engine Oil Temperature Sensor',
      k: () => ({
        summary: 'The engine oil temperature reading is missing or implausible.',
        severity: 'check',
        effects: ['Oil temperature display wrong', 'Some engines limit revs while cold for longer'],
        causes: ['Sensor', 'Wiring or connector'],
        checks: [CONNECTOR_CHECK, 'Check the oil level'],
      }),
    },
    {
      base: 220,
      name: 'Throttle/Pedal Position Sensor B',
      k: () => throttleKnowledge(),
    },
    {
      base: 225,
      name: 'Throttle/Pedal Position Sensor C',
      k: () => throttleKnowledge(),
    },
  ];
  for (const g of groups) {
    if (inRange(n, g.base, g.base + 4)) return { title: sensorTitle(g.name, n - g.base), ...g.k(n - g.base) };
  }
  return undefined;
};

function throttleKnowledge(): Omit<Knowledge, 'title'> {
  return {
    summary: 'The throttle or accelerator pedal position is measured by two or more sensors for safety. One reading is '
      + 'missing, implausible, or disagrees with the other.',
    severity: 'soon',
    drive: 'Drive carefully: the engine computer usually limits power (limp mode), which makes overtaking or joining fast '
      + 'traffic dangerous. Have it checked soon.',
    effects: ['Reduced power or limp mode', 'Engine may not respond to the pedal', 'Electronic throttle warning lamp'],
    causes: [
      'Accelerator pedal sensor or throttle body sensor',
      'Wiring or connector, especially at the pedal or throttle body',
      'Dirty throttle body',
    ],
    checks: [
      CONNECTOR_CHECK,
      'Watch the Accelerator Position reading in the Signals tab while pressing the pedal slowly: it should rise smoothly with no jumps',
      'Cleaning the throttle body and running a throttle relearn often helps',
    ],
  };
}

const warmUp: Family = ({ n }) => {
  if (n !== 125 && n !== 126 && n !== 128) return undefined;
  return {
    title: n === 128
      ? 'Coolant Thermostat (Coolant Temperature Below Thermostat Regulating Temperature)'
      : n === 125
        ? 'Insufficient Coolant Temperature for Closed Loop Fuel Control'
        : 'Insufficient Coolant Temperature for Stable Operation',
    summary: 'The engine takes too long to warm up, which almost always means the thermostat is stuck open.',
    severity: 'check',
    effects: ['Weak cabin heating', 'Higher fuel consumption, especially on short trips', 'Temperature gauge reads low'],
    causes: ['Thermostat stuck open', 'Coolant temperature sensor reading low', 'Low coolant level'],
    checks: [
      'Watch Engine Coolant Temperature in the Signals tab: it should reach about 80–95 °C within 10–15 minutes of driving',
      'Check the coolant level with the engine cold',
    ],
  };
};

const o2Sensor: Family = ({ n }) => {
  const groups: [number, number, number][] = [[130, 1, 1], [136, 1, 2], [142, 1, 3], [150, 2, 1], [156, 2, 2], [162, 2, 3]];
  const g = groups.find(([base]) => inRange(n, base, base + 5));
  if (!g) return undefined;
  const [base, bank, sensor] = g;
  const kind = n - base;
  const suffix = ['Circuit', 'Circuit Low Voltage', 'Circuit High Voltage', 'Circuit Slow Response', 'Circuit No Activity Detected', 'Heater Circuit'][kind];
  const upstream = sensor === 1;
  return {
    title: 'O2 Sensor ' + suffix + ' (Bank ' + bank + ' Sensor ' + sensor + ')',
    bank,
    sensor,
    summary: (upstream
      ? 'Sensor 1 sits before the catalytic converter and is what the engine computer uses to keep the fuel mixture right. '
      : 'Sensor ' + sensor + ' sits after the catalytic converter and checks that the converter is working. ')
      + (kind === 5
        ? 'Its built-in heater, which gets it working soon after a cold start, has a fault.'
        : kind === 3
          ? 'It is reacting too slowly, which usually means it is ageing.'
          : kind === 4
            ? 'Its signal is not changing at all.'
            : 'Its signal is missing or out of range.')
      + (bank === 2 ? ' Bank 2 is the side of a V engine without cylinder 1.' : ''),
    severity: upstream ? 'soon' : 'check',
    effects: upstream
      ? ['Higher fuel consumption', 'Rough running in some cases', 'Emissions inspection fail', 'Over time, catalytic converter damage']
      : ['Emissions inspection fail (catalyst self-test cannot run)', 'Usually nothing noticeable while driving'],
    causes: [
      'Ageing or contaminated oxygen sensor (they wear out, typically after 100,000–150,000 km)',
      'Exhaust leak before the sensor',
      'Wiring or connector, which sit near the hot exhaust',
      ...(kind === 1 ? ['An air leak making the mixture genuinely lean'] : []),
      ...(kind === 2 ? ['A genuinely rich mixture, such as a leaking injector'] : []),
      ...(kind === 5 ? ['Blown fuse for the sensor heaters'] : []),
    ],
    checks: [
      'Look and listen for exhaust leaks before the sensor',
      CONNECTOR_CHECK,
      upstream
        ? 'With a warm engine, O2 Sensor 1 Voltage in the Signals tab should swing quickly between about 0.1 and 0.9 V'
        : 'With a warm engine, a downstream sensor usually holds fairly steady; a copy of the upstream swings suggests a worn catalyst',
    ],
  };
};

const fuelTrim: Family = ({ n }) => {
  const bank1 = [170, 171, 172].indexOf(n);
  const bank2 = [173, 174, 175].indexOf(n);
  const idle = [2187, 2188, 2189, 2190].indexOf(n);
  const offIdle = [2177, 2178, 2179, 2180].indexOf(n);
  let lean: boolean | undefined;
  let bank: number;
  let where = '';
  if (bank1 >= 0) {
    bank = 1;
    lean = bank1 === 0 ? undefined : bank1 === 1;
  } else if (bank2 >= 0) {
    bank = 2;
    lean = bank2 === 0 ? undefined : bank2 === 1;
  } else if (idle >= 0) {
    bank = idle < 2 ? 1 : 2;
    lean = idle % 2 === 0;
    where = ' at Idle';
  } else if (offIdle >= 0) {
    bank = offIdle < 2 ? 1 : 2;
    lean = offIdle % 2 === 0;
    where = ' Off Idle';
  } else {
    return undefined;
  }
  const title = lean === undefined
    ? 'Fuel Trim Malfunction (Bank ' + bank + ')'
    : 'System Too ' + (lean ? 'Lean' : 'Rich') + where + ' (Bank ' + bank + ')';
  return {
    title,
    bank,
    summary: lean === undefined
      ? 'The engine computer has had to correct the fuel mixture further than it is allowed to.'
      : 'The engine computer has had to ' + (lean ? 'add' : 'remove') + ' more fuel than it is allowed to, because the mixture keeps coming out too '
        + (lean ? 'weak (too much air)' : 'strong (too much fuel)') + (where === ' at Idle' ? ' at idle' : '') + '.',
    severity: 'soon',
    effects: ['Hesitation or rough idle', 'Higher fuel consumption', 'Over time, catalytic converter damage'],
    causes: lean === false
      ? ['Leaking or dripping injector', 'Air flow sensor reading too high', 'Fuel pressure too high', 'Blocked air filter', 'Evaporative purge valve stuck open']
      : [
        'Air leak after the air flow sensor: vacuum hose, intake gasket, PCV valve',
        'Dirty air flow sensor reading too low',
        'Weak fuel pump or clogged fuel filter',
        'Clogged injectors',
        'Exhaust leak before the oxygen sensor',
      ],
    checks: [
      'Watch Short and Long Term Fuel Trim in the Signals tab: values beyond about ±10% show which way the mixture is off',
      lean === false
        ? 'Check the air filter first, then have injectors and fuel pressure tested'
        : 'If the trims are high at idle but settle when revving, look for a vacuum leak; if they rise under load, suspect fuel delivery',
    ],
  };
};

const injectors: Family = ({ n, ctx }) => {
  let cylinder: number | undefined;
  let title: string | undefined;
  let kind: 'circuit' | 'low' | 'high' | 'balance' = 'circuit';
  if (n === 200) {
    title = 'Injector Circuit';
  } else if (inRange(n, 201, 212)) {
    cylinder = n - 200;
    title = 'Injector Circuit - Cylinder ' + cylinder;
  } else if (inRange(n, 261, 296)) {
    cylinder = Math.floor((n - 261) / 3) + 1;
    const k = (n - 261) % 3;
    kind = k === 0 ? 'low' : k === 1 ? 'high' : 'balance';
    title = k === 2 ? 'Cylinder ' + cylinder + ' Contribution/Balance' : 'Cylinder ' + cylinder + ' Injector Circuit ' + (k === 0 ? 'Low' : 'High');
  } else {
    return undefined;
  }
  const k: Knowledge = {
    title,
    summary: kind === 'balance'
      ? 'The engine computer measures how much each cylinder contributes to turning the crankshaft. Cylinder '
        + cylinder + ' is contributing noticeably more or less than the others.'
      : 'The electrical circuit that opens the fuel injector' + (cylinder ? ' for cylinder ' + cylinder : '') + ' has a fault, so that cylinder may not be getting fuel properly.',
    severity: 'soon',
    effects: ['Misfire and rough running', 'Loss of power', 'Higher consumption or smoke', 'Limp mode on some cars'],
    causes: kind === 'balance'
      ? byFuel(ctx, ['Partly blocked or leaking injector', 'Low compression in that cylinder'], ['Worn or leaking injector (a very common diesel fault)', 'Low compression in that cylinder'])
      : ['Connector or wiring at the injector', 'Injector coil failed', 'Driver circuit in the engine computer (rare)'],
    checks: kind === 'balance'
      ? ['A workshop can run an injector balance or leak-off test']
      : [CONNECTOR_CHECK, 'A workshop can compare the injector\'s resistance with the others'],
  };
  if (cylinder !== undefined) k.cylinder = cylinder;
  return k;
};

const engineProtection: Family = ({ n }) => {
  switch (n) {
    case 217:
      return {
        title: 'Engine Coolant Over Temperature Condition',
        summary: 'The engine has overheated.',
        severity: 'stop',
        drive: 'Stop as soon as it is safe, switch off and let the engine cool. Do not open the coolant cap while hot. '
          + 'Driving on an overheating engine can warp the cylinder head or destroy the engine.',
        effects: ['Temperature gauge high, steam, or a sweet smell', 'Power may be cut to protect the engine'],
        causes: ['Low coolant from a leak', 'Cooling fan not working', 'Thermostat stuck closed', 'Failed water pump', 'Head gasket failure'],
        checks: ['Once cold, check the coolant level and look for leaks', 'Check that the cooling fan runs when the engine is hot'],
      };
    case 218:
      return {
        title: 'Transmission Fluid Over Temperature Condition',
        summary: 'The automatic transmission has overheated.',
        severity: 'stop',
        drive: 'Stop when safe and let it cool with the engine idling in Park, or switched off. Heavy loads, towing and '
          + 'hill climbing make it worse; continued driving can destroy the transmission.',
        effects: ['Shifting may change or the transmission may go into a protection mode'],
        causes: ['Low or old transmission fluid', 'Towing or heavy load', 'Blocked transmission cooler', 'Slipping torque converter or clutches'],
        checks: ['Have the fluid level and condition checked'],
      };
    case 219:
      return {
        title: 'Engine Overspeed Condition',
        summary: 'The engine went past its maximum allowed speed at some point.',
        severity: 'check',
        effects: ['Usually nothing now; repeated over-revving wears the engine'],
        causes: ['Changing down a gear at too high a speed', 'Revving hard in neutral', 'A slipping clutch or transmission'],
        checks: ['If it happened without over-revving, have the clutch or transmission checked'],
      };
    case 297:
      return {
        title: 'Vehicle Overspeed Condition',
        summary: 'The car was driven faster than its set maximum.',
        severity: 'info',
        effects: ['Informational'],
        causes: ['Driving above the programmed speed limit, or a speed sensor fault'],
        checks: ['If the car was not going that fast, check the vehicle speed sensor'],
      };
    case 298:
      return {
        title: 'Engine Oil Over Temperature Condition',
        summary: 'The engine oil has overheated, which thins it and reduces its protection.',
        severity: 'stop',
        drive: 'Stop as soon as it is safe and let the engine cool. Check the oil level once it is cold.',
        effects: ['Possible warning lamps and power reduction'],
        causes: ['Low oil level', 'Overheating engine', 'Oil cooler blocked', 'Very hard driving or towing in heat'],
        checks: ['Check the oil and coolant levels once cold'],
      };
    default:
      return undefined;
  }
};

const boost: Family = ({ n }) => {
  if (n === 234) {
    return {
      title: 'Turbocharger/Supercharger Overboost Condition',
      summary: 'The engine received more boost pressure than it should.',
      severity: 'soon',
      drive: 'Drive gently: excess boost can damage the engine, and the engine computer usually cuts power. '
        + 'Stop if the car surges or the engine sounds unusual.',
      effects: ['Sudden loss of power (limp mode)', 'Possible surging'],
      causes: ['Wastegate or variable vanes stuck', 'Boost control solenoid', 'Vacuum line to the turbo actuator', 'Boost pressure sensor reading wrong'],
      checks: ['Check the vacuum lines to the turbo actuator', 'Compare Boost Pressure in the Signals tab with what the car should make'],
    };
  }
  if (n === 299) {
    return {
      title: 'Turbocharger/Supercharger Underboost Condition',
      summary: 'The engine is getting less boost pressure than the engine computer asked for.',
      severity: 'soon',
      drive: 'Drive gently. If there is a loud whine, blue smoke or oil smoke, stop: the turbo may be failing.',
      effects: ['Noticeable loss of power', 'Limp mode', 'A whistle or hiss under acceleration if a hose is split'],
      causes: [
        'Boost leak: split or loose intercooler hose',
        'Sticking wastegate or variable vanes',
        'Boost control solenoid or vacuum line',
        'Worn turbocharger',
        'Blocked particulate filter or catalyst restricting the exhaust',
      ],
      checks: ['Check all hoses between the turbo, intercooler and engine for oil, splits and loose clips', 'Check the vacuum lines to the turbo actuator'],
    };
  }
  const groups: [number, string][] = [[235, 'Turbocharger Boost Sensor "A"'], [239, 'Turbocharger Boost Sensor "B"']];
  for (const [base, name] of groups) {
    if (inRange(n, base, base + 3)) {
      return {
        title: name + ' ' + ['Circuit', 'Circuit Range/Performance', 'Circuit Low', 'Circuit High'][n - base],
        summary: 'The boost pressure reading is missing or implausible, so boost cannot be controlled properly.',
        severity: 'soon',
        effects: ['Reduced power or limp mode'],
        causes: ['Boost pressure sensor, often clogged with oil', 'Wiring or connector', 'A real boost problem'],
        checks: [CONNECTOR_CHECK, 'Cleaning or replacing the sensor is the usual fix'],
      };
    }
  }
  const wastegate: [number, string][] = [[243, 'Turbocharger Wastegate Solenoid "A"'], [247, 'Turbocharger Wastegate Solenoid "B"']];
  for (const [base, name] of wastegate) {
    if (inRange(n, base, base + 3)) {
      return turboKnowledge(name + ['', ' Range/Performance', ' Low', ' High'][n - base], false);
    }
  }
  if (inRange(n, 2261, 2263)) {
    return turboKnowledge(
      ['Turbocharger Bypass Valve - Mechanical', 'Turbocharger Boost Pressure Not Detected - Mechanical', 'Turbocharger Boost System Performance'][n - 2261] as string,
      false,
    );
  }
  return undefined;
};

const pumpMetering: Family = ({ n }) => {
  if (!inRange(n, 251, 260)) return undefined;
  const letter = n <= 255 ? 'A' : 'B';
  const suffix = ['Malfunction', 'Range/Performance', 'Low', 'High', 'Intermittent'][(n - 251) % 5];
  return {
    title: 'Injection Pump Fuel Metering Control "' + letter + '" ' + suffix,
    summary: 'The part of the diesel injection pump that sets how much fuel is delivered is not responding properly.',
    severity: 'soon',
    effects: ['Hard starting, stalling or limp mode', 'Loss of power'],
    causes: ['Fuel metering valve on the pump', 'Air or dirt in the fuel', 'Wiring or connector', 'Worn high-pressure pump'],
    checks: ['Check the fuel filter and for air in the fuel lines', CONNECTOR_CHECK],
  };
};

const misfire: Family = ({ n, ctx }) => {
  if (!inRange(n, 300, 316)) return undefined;
  if (n === 315) {
    return {
      title: 'Crankshaft Position System Variation Not Learned',
      summary: 'The engine computer has not learned the small variations in the crankshaft sensor wheel it needs to detect misfires.',
      severity: 'check',
      effects: ['Misfire monitoring cannot run, which fails an emissions inspection'],
      causes: ['A relearn was not done after replacing the engine computer, crank sensor or battery on some cars'],
      checks: ['A workshop tool runs the relearn procedure'],
    };
  }
  const cylinder = inRange(n, 301, 312) ? n - 300 : undefined;
  const title = n === 300 ? 'Random/Multiple Cylinder Misfire Detected'
    : cylinder !== undefined ? 'Cylinder ' + cylinder + ' Misfire Detected'
      : n === 313 ? 'Misfire Detected with Low Fuel'
        : n === 314 ? 'Single Cylinder Misfire (Cylinder not Specified)'
          : 'Engine Misfire Detected on Startup (First 1000 Revolutions)';
  const k: Knowledge = {
    title,
    summary: (cylinder
      ? 'Cylinder ' + cylinder + ' is failing to fire properly.'
      : n === 300 ? 'Combustion is failing in several cylinders, or at random.'
        : n === 313 ? 'The engine misfired while the fuel level was low, most likely from fuel starvation.'
          : 'Combustion is failing in a cylinder.')
      + ' Unburnt fuel then reaches the catalytic converter and can overheat it.',
    severity: n === 313 ? 'check' : 'soon',
    drive: 'Drive gently and have it checked soon. If the engine warning lamp is flashing, the misfire is severe: stop as '
      + 'soon as it is safe, because unburnt fuel can overheat and destroy the catalytic converter.',
    effects: ['Shaking or rough running, especially at idle', 'Loss of power', 'Higher fuel consumption', 'Catalytic converter damage if it continues'],
    causes: n === 313
      ? ['Running very low on fuel', 'Fuel pump pickup starving on bends or hills']
      : [
        ...byFuel(
          ctx,
          ['Worn spark plug', 'Failing ignition coil', 'Blocked or leaking injector', 'Air leak near that cylinder'],
          ['Worn or leaking injector', 'Failing glow plug, if it happens mostly from cold'],
        ),
        'Low compression: valves, piston rings or head gasket',
        ...(n === 300 ? ['A fault common to all cylinders: fuel pressure, a large air leak, or poor-quality fuel'] : []),
      ],
    checks: n === 313
      ? ['Refuel and see whether it comes back']
      : [
        ...(isDiesel(ctx) ? [] : ['Check the spark plugs and their gap']),
        ...(cylinder && !isDiesel(ctx)
          ? ['Swap cylinder ' + cylinder + '\'s ignition coil with a neighbour\'s: if the misfire moves with it, the coil is at fault']
          : []),
        'If ignition parts are fine, have the injectors and compression tested',
        CLEAR_AND_RETEST,
      ],
  };
  if (cylinder !== undefined) k.cylinder = cylinder;
  return k;
};

const ignitionSensors: Family = ({ n }) => {
  if (inRange(n, 320, 324)) {
    return {
      title: ['Ignition/Distributor Engine Speed Input Circuit', 'Ignition/Distributor Engine Speed Input Circuit Range/Performance',
        'Ignition/Distributor Engine Speed Input Circuit No Signal', 'Ignition/Distributor Engine Speed Input Circuit Intermittent',
        'Knock Control System Error'][n - 320],
      summary: n === 324 ? 'The system that protects the engine from knocking has a fault.' : 'The engine speed signal is missing or unreliable.',
      severity: 'soon',
      effects: n === 324 ? ['Reduced power: ignition is retarded as a precaution'] : ['Stalling, cutting out or no start', 'Rev counter dropping'],
      causes: ['Sensor', 'Wiring or connector'],
      checks: [CONNECTOR_CHECK],
    };
  }
  if (inRange(n, 325, 334)) {
    const bank = n < 330 ? 1 : 2;
    const sensorN = n < 330 ? 1 : 2;
    return {
      title: sensorTitle('Knock Sensor ' + sensorN, (n - 325) % 5) + ' (Bank ' + bank + ')',
      bank,
      summary: 'The knock sensor listens for detonation, which damages engines. Its signal is missing or implausible, so '
        + 'the engine computer retards the ignition to be safe.',
      severity: 'check',
      drive: 'Safe to drive, but avoid hard acceleration and heavy loads until fixed. If you hear pinging or rattling '
        + 'under load, ease off and use the fuel grade the car specifies.',
      effects: ['Less power and higher fuel consumption'],
      causes: ['Knock sensor or its wiring', 'Sensor not tightened to the correct torque', 'Real engine noise being picked up'],
      checks: [CONNECTOR_CHECK],
    };
  }
  const posGroups: [number, string, string][] = [
    [335, 'Crankshaft Position Sensor "A"', ''],
    [340, 'Camshaft Position Sensor "A"', ' (Bank 1 or Single Sensor)'],
    [345, 'Camshaft Position Sensor "A"', ' (Bank 2)'],
    [365, 'Camshaft Position Sensor "B"', ' (Bank 1)'],
    [390, 'Camshaft Position Sensor "B"', ' (Bank 2)'],
  ];
  for (const [base, name, qualifier] of posGroups) {
    if (!inRange(n, base, base + 4)) continue;
    const crank = base === 335;
    const shaft = crank ? 'crankshaft' : 'camshaft';
    return {
      title: sensorTitle(name, n - base) + qualifier,
      summary: 'The ' + shaft + ' position sensor tells the engine computer where the engine is in its cycle. '
        + 'Its signal is missing or unreliable.',
      severity: 'soon',
      drive: crank
        ? 'The engine can cut out without warning, which is dangerous in traffic. Have it fixed before long journeys.'
        : 'Drive gently: starting may take longer and power may be reduced. Have it checked soon.',
      effects: crank
        ? ['Engine cutting out or not starting', 'Stalling when hot', 'Rev counter dropping to zero']
        : ['Longer cranking before the engine starts', 'Reduced power or rough running'],
      causes: ['Sensor', 'Wiring or connector', 'Metal debris on a magnetic sensor', 'Damaged sensor target wheel', ...(crank ? [] : ['Timing chain or belt problem'])],
      checks: [CONNECTOR_CHECK, 'Check the sensor tip for debris'],
    };
  }
  if (inRange(n, 350, 362)) {
    const coil = n === 350 ? undefined : String.fromCharCode(64 + n - 350);
    return {
      title: 'Ignition Coil ' + (coil ? '"' + coil + '" ' : '') + 'Primary/Secondary Circuit',
      summary: 'An ignition coil or its circuit has an electrical fault. On most engines coil A serves cylinder 1, coil B cylinder 2, and so on.',
      severity: 'soon',
      drive: 'Drive gently. If the engine shakes badly or the warning lamp flashes, stop: misfires can damage the catalytic converter.',
      effects: ['Misfire and rough running', 'Loss of power'],
      causes: ['Failed ignition coil', 'Wiring or connector', 'Worn spark plug overloading the coil'],
      checks: [CONNECTOR_CHECK, 'Swap the suspect coil with another and see whether the fault follows it'],
    };
  }
  if (inRange(n, 380, 384)) {
    return glowPlugKnowledge(['Glow Plug/Heater Circuit "A"', 'Glow Plug/Heater Indicator Circuit', 'Glow Plug/Heater Circuit "B"',
      'Glow Plug Control Module Control Circuit Low', 'Glow Plug Control Module Control Circuit High'][n - 380] as string);
  }
  return undefined;
};

function glowPlugKnowledge(title: string, cylinder?: number): Knowledge {
  const k: Knowledge = {
    title,
    summary: 'Diesel engines use glow plugs to heat the combustion chambers for cold starts. '
      + (cylinder ? 'The glow plug for cylinder ' + cylinder + ' or its circuit' : 'The glow plug system') + ' has a fault.',
    severity: 'check',
    drive: 'Safe to drive. In cold weather the engine may be hard to start, so fix it before winter.',
    effects: ['Hard starting when cold', 'White smoke and rough running just after a cold start'],
    causes: ['Burnt-out glow plug', 'Glow plug control module or relay', 'Wiring or connector'],
    checks: ['A workshop can measure each glow plug\'s resistance', CONNECTOR_CHECK],
  };
  if (cylinder !== undefined) k.cylinder = cylinder;
  return k;
}

const egr: Family = ({ n, ctx }) => {
  const titles: Record<number, string> = {
    400: 'Exhaust Gas Recirculation Flow',
    401: 'Exhaust Gas Recirculation Flow Insufficient Detected',
    402: 'Exhaust Gas Recirculation Flow Excessive Detected',
    403: 'Exhaust Gas Recirculation Control Circuit',
    404: 'Exhaust Gas Recirculation Control Circuit Range/Performance',
    405: 'Exhaust Gas Recirculation Sensor "A" Circuit Low',
    406: 'Exhaust Gas Recirculation Sensor "A" Circuit High',
    407: 'Exhaust Gas Recirculation Sensor "B" Circuit Low',
    408: 'Exhaust Gas Recirculation Sensor "B" Circuit High',
    409: 'Exhaust Gas Recirculation Sensor "A" Circuit',
    489: 'Exhaust Gas Recirculation Control Circuit Low',
    490: 'Exhaust Gas Recirculation Control Circuit High',
    2413: 'Exhaust Gas Recirculation System Performance',
    2457: 'Exhaust Gas Recirculation Cooling System Performance',
  };
  const title = titles[n];
  if (!title) return undefined;
  return {
    title,
    summary: 'The exhaust gas recirculation (EGR) valve feeds some exhaust back into the intake to reduce NOx emissions. '
      + (n === 402 ? 'Too much is flowing, which usually means the valve is stuck open.' : 'It is not flowing or responding as expected.'),
    severity: isDiesel(ctx) ? 'soon' : 'check',
    effects: [
      'Rough idle or stalling if stuck open',
      'Loss of power or limp mode, especially on diesels',
      'Higher NOx emissions and an emissions inspection fail',
      ...(isDiesel(ctx) ? ['More soot, which fills the particulate filter faster'] : []),
    ],
    causes: [
      'Carbon build-up in the EGR valve or passages (very common, especially on diesels used for short trips)',
      'EGR valve motor or position sensor',
      'Vacuum hose or control solenoid on older designs',
      'Wiring or connector',
    ],
    checks: ['Cleaning or replacing the EGR valve is the usual fix', CONNECTOR_CHECK, 'Regular longer drives reduce carbon build-up'],
  };
};

const secondaryAir: Family = ({ n }) => {
  if (!inRange(n, 410, 419)) return undefined;
  const titles: Record<number, string> = {
    410: 'Secondary Air Injection System',
    411: 'Secondary Air Injection System Incorrect Flow Detected',
    412: 'Secondary Air Injection System Switching Valve "A" Circuit',
    413: 'Secondary Air Injection System Switching Valve "A" Circuit Open',
    414: 'Secondary Air Injection System Switching Valve "A" Circuit Shorted',
    418: 'Secondary Air Injection System Control "A" Circuit',
  };
  return {
    title: titles[n] ?? 'Secondary Air Injection System',
    summary: 'Some petrol cars pump air into the exhaust after a cold start to heat the catalytic converter quickly. That system has a fault.',
    severity: 'check',
    effects: ['Higher emissions just after cold starts', 'Emissions inspection fail', 'Usually nothing noticeable while driving'],
    causes: ['Air pump failed, often from moisture', 'Check valve or combination valve stuck', 'Pump relay or fuse', 'Vacuum hose'],
    checks: ['Listen for the air pump running for about a minute after a cold start', 'Check the pump fuse and relay'],
  };
};

const catalyst: Family = ({ n }) => {
  const titles: Record<number, [string, number]> = {
    420: ['Catalyst System Efficiency Below Threshold (Bank 1)', 1],
    421: ['Warm Up Catalyst Efficiency Below Threshold (Bank 1)', 1],
    422: ['Main Catalyst Efficiency Below Threshold (Bank 1)', 1],
    430: ['Catalyst System Efficiency Below Threshold (Bank 2)', 2],
    431: ['Warm Up Catalyst Efficiency Below Threshold (Bank 2)', 2],
    432: ['Main Catalyst Efficiency Below Threshold (Bank 2)', 2],
  };
  const t = titles[n];
  if (!t) return undefined;
  return {
    title: t[0],
    bank: t[1],
    summary: 'The catalytic converter is no longer cleaning the exhaust well enough, judged by comparing the oxygen sensors before and after it.',
    severity: 'check',
    drive: 'Usually safe to drive. Stop and have it checked if you smell rotten eggs strongly, hear rattling from under the car, '
      + 'or the car loses power: a blocked converter can overheat.',
    effects: ['Emissions inspection fail', 'Sometimes a sulphur smell', 'Loss of power if the converter is blocked'],
    causes: [
      'Worn-out catalytic converter',
      'Damage from misfires or oil burning: fix those first or a new converter fails too',
      'Exhaust leak near the sensors',
      'Ageing downstream oxygen sensor',
    ],
    checks: ['Look for misfire or fuel trim codes and fix them first', 'Check for exhaust leaks', 'Have the converter tested before replacing it'],
  };
};

const evap: Family = ({ n }) => {
  const titles: Record<number, string> = {
    440: 'Evaporative Emission System',
    441: 'Evaporative Emission System Incorrect Purge Flow',
    442: 'Evaporative Emission System Leak Detected (Small Leak)',
    443: 'Evaporative Emission System Purge Control Valve Circuit',
    444: 'Evaporative Emission System Purge Control Valve Circuit Open',
    445: 'Evaporative Emission System Purge Control Valve Circuit Shorted',
    446: 'Evaporative Emission System Vent Control Circuit',
    447: 'Evaporative Emission System Vent Control Circuit Open',
    448: 'Evaporative Emission System Vent Control Circuit Shorted',
    449: 'Evaporative Emission System Vent Valve/Solenoid Circuit',
    450: 'Evaporative Emission System Pressure Sensor/Switch',
    451: 'Evaporative Emission System Pressure Sensor/Switch Range/Performance',
    452: 'Evaporative Emission System Pressure Sensor/Switch Low',
    453: 'Evaporative Emission System Pressure Sensor/Switch High',
    454: 'Evaporative Emission System Pressure Sensor/Switch Intermittent',
    455: 'Evaporative Emission System Leak Detected (Large Leak/No Flow)',
    456: 'Evaporative Emission System Leak Detected (Very Small Leak)',
    457: 'Evaporative Emission System Leak Detected (Fuel Cap Loose/Off)',
    496: 'Evaporative Emission System High Purge Flow',
    497: 'Evaporative Emission System Low Purge Flow',
  };
  const title = titles[n];
  if (!title) return undefined;
  const leak = [442, 455, 456, 457].includes(n);
  return {
    title,
    summary: 'The evaporative emission (EVAP) system catches fuel vapour from the tank and burns it in the engine instead of '
      + 'letting it escape. ' + (leak ? 'The car has found a leak in it.' : 'A valve, sensor or its circuit has a fault.'),
    severity: 'check',
    effects: [
      'Usually nothing noticeable while driving',
      'Possible smell of fuel',
      ...(n === 496 ? ['Hard starting or rough idle just after refuelling'] : []),
      'Emissions inspection fail',
    ],
    causes: leak
      ? ['Fuel cap loose, missing or with a worn seal (the most common cause)', 'Cracked vapour hose', 'Purge or vent valve not sealing', 'Charcoal canister damage']
      : ['Purge or vent valve', 'Pressure sensor', 'Wiring or connector', 'Blocked or damaged vapour hose'],
    checks: leak
      ? ['Tighten the fuel cap until it clicks, or replace a worn one, then drive for a few days', 'A workshop can smoke-test the system for leaks']
      : [CONNECTOR_CHECK, 'A workshop can test the purge and vent valves'],
  };
};

const fuelLevel: Family = ({ n }) => {
  if (!inRange(n, 460, 464)) return undefined;
  return {
    title: 'Fuel Level Sensor "A" ' + (SENSOR_SUFFIX[n - 460] ?? 'Circuit'),
    summary: 'The fuel level reading is missing or implausible.',
    severity: 'check',
    effects: ['Fuel gauge wrong or jumping', 'Leak detection (EVAP) self-test cannot run', 'Risk of running out of fuel'],
    causes: ['Fuel level sender in the tank', 'Wiring or connector'],
    checks: ['Keep track of fuel by distance until fixed', CONNECTOR_CHECK],
  };
};

const exhaustPressure: Family = ({ n }) => {
  if (!inRange(n, 470, 479)) return undefined;
  const sensor = n <= 474;
  return {
    title: (sensor ? 'Exhaust Pressure Sensor' : 'Exhaust Pressure Control Valve')
      + ['', ' Range/Performance', ' Low', ' High', ' Intermittent'][(n - 470) % 5],
    summary: sensor
      ? 'The exhaust back-pressure reading is missing or implausible.'
      : 'The valve that controls exhaust back-pressure has a fault.',
    severity: 'check',
    effects: ['Possible reduced power', 'Affects particulate filter and turbo control on diesels'],
    causes: ['Sensor or valve', 'Blocked sensor pipe', 'Wiring or connector'],
    checks: [CONNECTOR_CHECK, 'Check the small pipe to the sensor for blockage or splits'],
  };
};

const coolingFan: Family = ({ n }) => {
  const titles: Record<number, string> = {
    480: 'Fan 1 Control Circuit',
    481: 'Fan 2 Control Circuit',
    482: 'Fan 3 Control Circuit',
    483: 'Fan Performance',
    484: 'Fan Circuit Over Current',
    485: 'Fan Power/Ground Circuit',
    691: 'Fan 1 Control Circuit Low',
    692: 'Fan 1 Control Circuit High',
    693: 'Fan 2 Control Circuit Low',
    694: 'Fan 2 Control Circuit High',
  };
  const title = titles[n];
  if (!title) return undefined;
  return {
    title,
    summary: 'The electric cooling fan, or its control circuit, has a fault.',
    severity: 'soon',
    drive: 'Watch the temperature gauge, especially in traffic, where the fan matters most. Stop if it rises towards hot.',
    effects: ['Overheating in slow traffic', 'Air conditioning may cool poorly'],
    causes: ['Fan motor', 'Fan relay, fuse or control module', 'Wiring or connector'],
    checks: ['With the engine hot and idling, the fan should come on', 'Check the fan fuses and relay'],
  };
};

const speedIdle: Family = ({ n }) => {
  if (inRange(n, 500, 503)) {
    return {
      title: ['Vehicle Speed Sensor "A"', 'Vehicle Speed Sensor "A" Range/Performance', 'Vehicle Speed Sensor "A" Circuit Low Input',
        'Vehicle Speed Sensor "A" Intermittent/Erratic/High'][n - 500],
      summary: 'The road speed signal is missing or unreliable.',
      severity: 'soon',
      effects: ['Speedometer wrong or dead', 'Cruise control unavailable', 'Automatic gearbox may shift badly', 'ABS or stability warning lamps on some cars'],
      causes: ['Speed sensor (on many cars this is an ABS wheel speed sensor)', 'Wiring or connector', 'ABS module fault'],
      checks: [CONNECTOR_CHECK, 'Compare Vehicle Speed in the Signals tab with GPS Speed if location is on'],
    };
  }
  if (n === 504) {
    return {
      title: 'Brake Switch "A"/"B" Correlation',
      summary: 'The two brake pedal switch signals disagree.',
      severity: 'soon',
      effects: ['Brake lights may not work', 'Cruise control unavailable', 'Engine power may be limited'],
      causes: ['Brake light switch out of adjustment or failed', 'Blown brake light fuse'],
      checks: ['Ask someone to check the brake lights come on when you press the pedal'],
    };
  }
  if (n === 571) {
    return {
      title: 'Brake Switch "A" Circuit',
      summary: 'The brake pedal switch signal has a fault.',
      severity: 'soon',
      effects: ['Brake lights may not work', 'Cruise control unavailable'],
      causes: ['Brake light switch', 'Fuse or wiring'],
      checks: ['Check the brake lights work'],
    };
  }
  if (inRange(n, 505, 511)) {
    const titles: Record<number, string> = {
      505: 'Idle Air Control System',
      506: 'Idle Air Control System RPM Lower Than Expected',
      507: 'Idle Air Control System RPM Higher Than Expected',
      508: 'Idle Air Control System Circuit Low',
      509: 'Idle Air Control System Circuit High',
      510: 'Closed Throttle Position Switch',
      511: 'Idle Air Control Circuit',
    };
    const title = titles[n];
    if (!title) return undefined;
    return {
      title,
      summary: 'The engine is not holding the idle speed it is aiming for.',
      severity: 'check',
      effects: ['Idle too high, too low or hunting', 'Stalling when coming to a stop'],
      causes: ['Dirty throttle body or idle valve', 'Vacuum leak (idle too high)', 'Idle control valve or its wiring'],
      checks: ['Clean the throttle body', 'Listen for hissing vacuum leaks', 'Watch Engine Speed at idle in the Signals tab'],
    };
  }
  return undefined;
};

const oilPressure: Family = ({ n }) => {
  if (!inRange(n, 520, 524)) return undefined;
  if (n === 524) {
    return {
      title: 'Engine Oil Pressure Too Low',
      summary: 'The engine is not getting enough oil pressure to protect its bearings.',
      severity: 'stop',
      drive: 'Stop immediately when safe and switch the engine off. Running without oil pressure destroys an engine within minutes. '
        + 'Check the oil level; do not drive on if the oil warning lamp stays on.',
      effects: ['Oil pressure warning lamp', 'Ticking or knocking from the engine'],
      causes: ['Low oil level', 'Wrong or very old oil', 'Failing oil pump or blocked pickup', 'Worn bearings', 'Faulty pressure sensor'],
      checks: ['Check the oil level with the car level and the engine off', 'Have the pressure measured with a gauge before driving further'],
    };
  }
  return {
    title: 'Engine Oil Pressure Sensor/Switch ' + (SENSOR_SUFFIX[n - 520] ?? 'Circuit'),
    summary: 'The oil pressure reading is missing or implausible. The oil pressure may be fine, but the car cannot tell.',
    severity: 'soon',
    drive: 'Check the oil level before driving further. If the oil warning lamp comes on or the engine knocks, stop immediately.',
    effects: ['Oil warning lamp may flicker or stay on', 'Real low pressure would go unnoticed'],
    causes: ['Oil pressure sensor or switch (often leaking oil)', 'Wiring or connector', 'Real low oil pressure'],
    checks: ['Check the oil level', CONNECTOR_CHECK],
  };
};

const acRefrigerant: Family = ({ n }) => {
  if (!inRange(n, 530, 534)) return undefined;
  return {
    title: n === 534 ? 'A/C Refrigerant Charge Loss' : 'A/C Refrigerant Pressure Sensor "A" ' + (SENSOR_SUFFIX[n - 530] ?? 'Circuit'),
    summary: n === 534
      ? 'The air conditioning has lost refrigerant.'
      : 'The air conditioning pressure reading is missing or implausible, so the compressor is switched off to protect it.',
    severity: 'info',
    effects: ['Air conditioning not cooling'],
    causes: n === 534 ? ['Leak in the air conditioning system'] : ['Pressure sensor', 'Wiring or connector', 'Low refrigerant'],
    checks: ['Have the air conditioning system checked and leak-tested'],
  };
};

const exhaustTemp: Family = ({ n }) => {
  const groups: Record<number, [number, number, string]> = {
    544: [1, 1, 'Circuit'], 545: [1, 1, 'Circuit Low'], 546: [1, 1, 'Circuit High'],
    547: [2, 1, 'Circuit'], 548: [2, 1, 'Circuit Low'], 549: [2, 1, 'Circuit High'],
    2031: [1, 2, 'Circuit'], 2032: [1, 2, 'Circuit Low'], 2033: [1, 2, 'Circuit High'],
    2080: [1, 1, 'Circuit Range/Performance'], 2081: [1, 1, 'Circuit Intermittent'],
    2084: [1, 2, 'Circuit Range/Performance'], 2085: [1, 2, 'Circuit Intermittent'],
  };
  const g = groups[n];
  if (n === 2428 || n === 2429) {
    return {
      title: 'Exhaust Gas Temperature Too High (Bank ' + (n === 2428 ? 1 : 2) + ')',
      bank: n === 2428 ? 1 : 2,
      summary: 'The exhaust is hotter than it should be.',
      severity: 'soon',
      drive: 'Ease off and let it cool. Continuous high exhaust temperature can damage the turbo, catalyst or particulate filter.',
      effects: ['Power reduction to protect components'],
      causes: ['Heavy load or towing', 'Blocked particulate filter or catalyst', 'Fuelling fault', 'Sensor reading wrong'],
      checks: ['Look for other exhaust or fuelling codes'],
    };
  }
  if (!g) return undefined;
  const [bank, sensor, suffix] = g;
  return {
    title: 'Exhaust Gas Temperature Sensor ' + suffix + ' (Bank ' + bank + ' Sensor ' + sensor + ')',
    bank,
    sensor,
    summary: 'An exhaust temperature reading is missing or implausible. These sensors protect the turbo and manage particulate filter cleaning.',
    severity: 'check',
    effects: ['Particulate filter cleaning (regeneration) may be suspended', 'Possible power reduction'],
    causes: ['Sensor (they fail from heat)', 'Wiring or connector near the exhaust'],
    checks: [CONNECTOR_CHECK],
  };
};

const electrical: Family = ({ n }) => {
  if (inRange(n, 560, 563)) {
    return {
      title: ['System Voltage', 'System Voltage Unstable', 'System Voltage Low', 'System Voltage High'][n - 560],
      summary: n === 563
        ? 'The electrical system voltage is too high, which can damage electronics.'
        : 'The electrical system voltage is too low or unstable for the electronics to work reliably.',
      severity: 'soon',
      effects: ['Several unrelated warning lamps and codes', 'Hard starting', 'The car may stop if the battery is not being charged'],
      causes: n === 563
        ? ['Alternator voltage regulator', 'Jump-starting with the wrong voltage']
        : ['Weak or old battery', 'Alternator not charging', 'Loose or corroded battery terminals or engine ground strap'],
      checks: [
        'The Battery reading in the Signals tab should be about 12.4–12.7 V with the engine off and 13.5–14.7 V running',
        'Check the battery terminals are clean and tight',
        'Have the battery and charging system tested',
      ],
    };
  }
  if (inRange(n, 615, 617)) {
    return {
      title: 'Starter Relay Circuit' + ['', ' Low', ' High'][n - 615],
      summary: 'The circuit the engine computer uses to switch the starter motor has a fault.',
      severity: 'soon',
      effects: ['Engine may not crank, or starter may keep running briefly'],
      causes: ['Starter relay', 'Wiring or connector', 'Clutch or brake pedal switch that enables starting'],
      checks: [CONNECTOR_CHECK],
    };
  }
  if (inRange(n, 620, 626)) {
    const titles = ['Generator Control Circuit', 'Generator Lamp "L" Terminal Circuit', 'Generator Field "F" Terminal Circuit',
      'Generator Lamp Control Circuit', 'Fuel Cap Lamp Control Circuit', 'Generator Field Terminal Circuit Low', 'Generator Field Terminal Circuit High'];
    if (n === 624) {
      return {
        title: titles[4],
        summary: 'The circuit for the "check fuel cap" warning lamp has a fault.',
        severity: 'info',
        effects: ['That warning lamp may not work'],
        causes: ['Wiring or instrument cluster'],
        checks: [CONNECTOR_CHECK],
      };
    }
    return {
      title: titles[n - 620],
      summary: 'The engine computer has lost proper control of, or feedback from, the alternator that charges the battery.',
      severity: 'soon',
      drive: 'If the battery warning lamp is on, the car is running on battery power alone and will stop when it is flat. '
        + 'Avoid long drives and switch off unnecessary electrical loads.',
      effects: ['Battery warning lamp', 'Battery not charging', 'Car stopping when the battery runs flat'],
      causes: ['Alternator or its regulator', 'Wiring or connector at the alternator', 'Worn drive belt'],
      checks: ['Watch the Battery reading in the Signals tab: it should rise to about 13.5–14.7 V with the engine running', 'Check the alternator drive belt'],
    };
  }
  if (inRange(n, 627, 629)) {
    return {
      title: 'Fuel Pump "A" Control Circuit' + ['/Open', ' Low', ' High'][n - 627],
      summary: 'The circuit that switches the fuel pump has an electrical fault.',
      severity: 'soon',
      drive: 'The engine may stop suddenly or not restart. Avoid long trips until it is fixed.',
      effects: ['Engine cutting out or not starting', 'Loss of power'],
      causes: ['Fuel pump relay or control module', 'Wiring or connector', 'Failing fuel pump'],
      checks: ['Check the fuel pump fuse and relay', CONNECTOR_CHECK],
    };
  }
  const refs: Record<number, string> = { 641: 'A', 651: 'B', 697: 'C' };
  if (refs[n]) {
    return {
      title: 'Sensor Reference Voltage "' + refs[n] + '" Circuit/Open',
      summary: 'Several sensors share a 5-volt supply from the engine computer, and that supply is faulty. '
        + 'A short in any one of those sensors or their wiring pulls it down for all of them.',
      severity: 'soon',
      drive: 'Drive carefully: several sensors may stop working at once, often including the accelerator pedal, which triggers limp mode.',
      effects: ['Several sensor codes at the same time', 'Limp mode'],
      causes: ['A shorted sensor on that supply', 'Chafed wiring', 'Water in a connector', 'Engine computer (rare)'],
      checks: ['Look at which other sensor codes are present: they share the supply', 'Unplug those sensors one by one: the one that restores the supply is at fault'],
    };
  }
  if (n === 650) {
    return {
      title: 'Malfunction Indicator Lamp (MIL) Control Circuit',
      summary: 'The circuit for the engine warning lamp has a fault, so the lamp may not light when it should.',
      severity: 'check',
      effects: ['Engine warning lamp not working, hiding other faults'],
      causes: ['Bulb or LED in the instrument cluster', 'Wiring or instrument cluster'],
      checks: ['The lamp should light briefly when the ignition is switched on'],
    };
  }
  if (inRange(n, 670, 683)) {
    if (n === 670) return glowPlugKnowledge('Glow Plug Module Control Circuit');
    if (n === 683) return glowPlugKnowledge('Glow Plug Control Module to PCM Communication Circuit');
    return glowPlugKnowledge('Cylinder ' + (n - 670) + ' Glow Plug Circuit', n - 670);
  }
  if (inRange(n, 685, 690)) {
    return {
      title: ['ECM/PCM Power Relay Control Circuit/Open', 'ECM/PCM Power Relay Control Circuit Low', 'ECM/PCM Power Relay Control Circuit High',
        'ECM/PCM Power Relay Sense Circuit/Open', 'ECM/PCM Power Relay Sense Circuit Low', 'ECM/PCM Power Relay Sense Circuit High'][n - 685],
      summary: 'The relay that powers the engine computer and its sensors has a fault.',
      severity: 'soon',
      effects: ['Engine may not start or may cut out', 'Battery drain if the relay stays on'],
      causes: ['Main relay', 'Fuse, wiring or connector', 'Battery or ground connection'],
      checks: ['Check the battery terminals and engine ground strap', 'Check the main relay and its fuse'],
    };
  }
  return undefined;
};

const controlModule: Family = ({ n }) => {
  const titles: Record<number, string> = {
    600: 'Serial Communication Link',
    601: 'Internal Control Module Memory Check Sum Error',
    602: 'Control Module Programming Error',
    603: 'Internal Control Module Keep Alive Memory (KAM) Error',
    604: 'Internal Control Module Random Access Memory (RAM) Error',
    605: 'Internal Control Module Read Only Memory (ROM) Error',
    606: 'Control Module Processor',
    607: 'Control Module Performance',
    610: 'Control Module Vehicle Options Error',
  };
  const title = titles[n];
  if (!title) return undefined;
  return {
    title,
    summary: n === 603
      ? 'The engine computer lost the memory it keeps with the ignition off. This is expected after the battery has been disconnected or run flat.'
      : 'The engine computer has detected a fault in itself, its software or its communication.',
    severity: n === 603 ? 'info' : 'soon',
    drive: n === 603
      ? 'Safe to drive. The engine relearns its settings over the next drives; clear the code and see whether it returns.'
      : 'The car may run normally or go into limp mode. Have it checked soon, and avoid long trips if it runs badly.',
    effects: n === 603
      ? ['Idle or shifting may feel different for a few drives while settings are relearned']
      : ['Limp mode, stalling or no start', 'Other unrelated codes'],
    causes: n === 603
      ? ['Battery disconnected or flat', 'Poor battery connection']
      : ['Low or unstable supply voltage', 'Poor ground connection', 'Software needing an update', 'Water damage or a failing engine computer'],
    checks: n === 603
      ? ['If it keeps coming back, check the battery and its terminals']
      : ['Rule out battery and ground connection problems first', 'Ask a dealer whether a software update exists for the engine computer'],
  };
};

const transmission: Family = ({ n }) => {
  if (!inRange(n, 700, 999) && !inRange(n, 2700, 2899)) return undefined;
  return transmissionKnowledge(n);
};

function transmissionKnowledge(n: number): Knowledge {
  const titles: Record<number, string> = {
    700: 'Transmission Control System (MIL Request)',
    701: 'Transmission Control System Range/Performance',
    702: 'Transmission Control System Electrical',
    705: 'Transmission Range Sensor "A" Circuit (PRNDL Input)',
    706: 'Transmission Range Sensor "A" Circuit Range/Performance',
    710: 'Transmission Fluid Temperature Sensor "A" Circuit',
    711: 'Transmission Fluid Temperature Sensor "A" Circuit Range/Performance',
    715: 'Input/Turbine Speed Sensor "A" Circuit',
    716: 'Input/Turbine Speed Sensor "A" Circuit Range/Performance',
    717: 'Input/Turbine Speed Sensor "A" Circuit No Signal',
    720: 'Output Speed Sensor Circuit',
    721: 'Output Speed Sensor Circuit Range/Performance',
    722: 'Output Speed Sensor Circuit No Signal',
    725: 'Engine Speed Input Circuit',
    730: 'Incorrect Gear Ratio',
    731: 'Gear 1 Incorrect Ratio',
    732: 'Gear 2 Incorrect Ratio',
    733: 'Gear 3 Incorrect Ratio',
    734: 'Gear 4 Incorrect Ratio',
    735: 'Gear 5 Incorrect Ratio',
    736: 'Reverse Incorrect Ratio',
    740: 'Torque Converter Clutch Solenoid Circuit/Open',
    741: 'Torque Converter Clutch Solenoid Circuit Performance/Stuck Off',
    742: 'Torque Converter Clutch Solenoid Circuit Stuck On',
    743: 'Torque Converter Clutch Solenoid Circuit Electrical',
    744: 'Torque Converter Clutch Solenoid Circuit Intermittent',
    780: 'Shift Error',
    868: 'Transmission Fluid Pressure Low',
    869: 'Transmission Fluid Pressure High',
    894: 'Transmission Component Slipping',
    897: 'Transmission Fluid Deteriorated',
  };
  let title = titles[n];
  if (!title && inRange(n, 750, 774)) {
    const solenoid = String.fromCharCode(65 + Math.floor((n - 750) / 5));
    const suffix = ['', ' Performance/Stuck Off', ' Stuck On', ' Electrical', ' Intermittent'][(n - 750) % 5];
    title = 'Shift Solenoid "' + solenoid + '"' + suffix;
  }
  const k: Knowledge = {
    summary: n === 700
      ? 'The transmission control module has found a fault and asked the engine computer to light the warning lamp. '
        + 'The detail is stored in the transmission module itself.'
      : n === 897
        ? 'The transmission fluid has aged beyond its limit, judged by how hot and how long it has run.'
        : 'A fault in the automatic or automated transmission, its sensors or its hydraulic control.',
    severity: n === 897 ? 'check' : 'soon',
    drive: 'Drive gently and avoid towing or heavy loads. Many transmissions lock into one gear to protect themselves ("limp mode"). '
      + 'If it slips, bangs into gear or smells burnt, stop and have it recovered rather than driven.',
    effects: ['Harsh, late or missing shifts', 'Stuck in one gear', 'Transmission warning lamp'],
    causes: [
      'Low, old or wrong transmission fluid',
      'Shift solenoid or valve body',
      'Speed or range sensor, or its wiring',
      'Internal wear (clutches or torque converter)',
    ],
    checks: [
      'Have the fluid level and condition checked',
      ...(n === 700 ? ['Have the transmission module read with a tool that talks to it directly: this app reads the engine computer\'s list'] : []),
      CONNECTOR_CHECK,
    ],
  };
  if (title) k.title = title;
  return k;
}

const hybrid: Family = ({ digits }) => {
  if (!/^0[A-F]/.test(digits)) return undefined;
  const titles: Record<string, string> = {
    '0A7F': 'Hybrid/EV Battery Pack Deterioration',
    '0A80': 'Replace Hybrid/EV Battery Pack',
    '0AA6': 'Hybrid/EV Battery Voltage System Isolation Fault',
  };
  const title = titles[digits];
  const isolation = digits === '0AA6';
  const k: Knowledge = {
    summary: isolation
      ? 'The high-voltage system has lost its insulation from the car body somewhere. This is a safety fault.'
      : 'A fault in the hybrid or electric drive system: the high-voltage battery, motor, inverter or their cooling.',
    severity: 'soon',
    drive: isolation
      ? 'Have it checked by a qualified workshop before further use. Do not touch orange high-voltage cables or components.'
      : 'The car may limit power or refuse to start. Have it checked by a workshop qualified for high-voltage systems.',
    effects: ['Reduced power or electric range', 'Hybrid system warning', 'The car may not start'],
    causes: ['High-voltage battery ageing or a failed cell', 'Battery cooling fan or filter blocked', 'Inverter, motor or cabling fault'],
    checks: ['Never open high-voltage components yourself', 'Check the battery cooling air intake (often under the rear seat) is not blocked'],
  };
  if (title) k.title = title;
  return k;
};

const aftertreatment: Family = ({ digits, n, ctx }) => {
  const dpfTitles: Record<string, string> = {
    '2002': 'Diesel Particulate Filter Efficiency Below Threshold (Bank 1)',
    '2003': 'Diesel Particulate Filter Efficiency Below Threshold (Bank 2)',
    '242F': 'Diesel Particulate Filter Restriction - Ash Accumulation',
    '244A': 'Diesel Particulate Filter Differential Pressure Too Low',
    '244B': 'Diesel Particulate Filter Differential Pressure Too High',
    '2452': 'Diesel Particulate Filter Pressure Sensor "A" Circuit',
    '2453': 'Diesel Particulate Filter Pressure Sensor "A" Circuit Range/Performance',
    '2454': 'Diesel Particulate Filter Pressure Sensor "A" Circuit Low',
    '2455': 'Diesel Particulate Filter Pressure Sensor "A" Circuit High',
    '2456': 'Diesel Particulate Filter Pressure Sensor "A" Circuit Intermittent',
    '2458': 'Diesel Particulate Filter Regeneration Duration',
    '2459': 'Diesel Particulate Filter Regeneration Frequency',
    '2463': 'Diesel Particulate Filter Restriction - Soot Accumulation',
  };
  const dpf = dpfTitles[digits];
  if (dpf) {
    const sensor = /Sensor/.test(dpf);
    const low = digits === '244A' || digits === '2002' || digits === '2003';
    return {
      title: dpf,
      summary: 'The diesel particulate filter (DPF) traps soot and burns it off now and then ("regeneration"), which needs a spell of steady driving at speed. '
        + (sensor
          ? 'The pressure sensor that tells how full the filter is has a fault.'
          : low
            ? 'The pressure across the filter is lower than it should be, which can mean it is cracked, missing, or the sensor pipes are leaking.'
            : 'The filter is fuller than it should be, or is having to clean itself too often or for too long.'),
      severity: 'soon',
      drive: low || sensor
        ? 'Usually safe to drive for now, but have it checked soon: without a working measurement the car cannot tell when the filter needs cleaning.'
        : 'A 20–30 minute drive at a steady 60–110 km/h (motorway or main road) often lets the filter clean itself. '
          + 'If the car is in limp mode or the warning stays on afterwards, have a workshop clean it: a filter that gets too full can no longer be cleaned safely.',
      effects: ['DPF warning lamp', 'Reduced power or limp mode', 'Higher fuel consumption', 'Cooling fan running after switching off, during a regeneration'],
      causes: sensor
        ? ['Pressure sensor', 'Split, blocked or swapped sensor pipes', 'Wiring or connector']
        : low
          ? ['Split or disconnected sensor pipes', 'Cracked or removed filter', 'Pressure sensor']
          : ['Mostly short trips, so regeneration never finishes', 'An underlying fault making extra soot: EGR, injectors, turbo', 'Ash build-up late in the filter\'s life', 'Wrong engine oil (high-ash)'],
      checks: [
        'The Signals tab shows DPF Differential Pressure if the car reports it: it rises as the filter fills and drops after cleaning',
        'Look for other codes that would cause extra soot and fix those first',
        ...(sensor || low ? ['Check the small pipes to the pressure sensor for splits and blockages'] : []),
      ],
    };
  }
  const scrTitles: Record<string, string> = {
    '203F': 'Reductant Level Too Low',
    '2047': 'Reductant Injector Circuit/Open (Bank 1 Unit 1)',
    '2048': 'Reductant Injector Circuit Low (Bank 1 Unit 1)',
    '2049': 'Reductant Injector Circuit High (Bank 1 Unit 1)',
    '204F': 'Reductant System Performance (Bank 1)',
    '20B9': 'Reductant Heater "A" Control Circuit/Open',
    '20E8': 'Reductant Pressure Too Low',
    '20EE': 'SCR NOx Catalyst Efficiency Below Threshold (Bank 1)',
  };
  const scr = scrTitles[digits];
  if (scr) {
    return {
      title: scr,
      summary: 'The SCR system injects a urea solution (AdBlue/DEF) into the exhaust to remove NOx. '
        + (digits === '203F' ? 'The tank is low.' : 'Part of that system is not working as it should.'),
      severity: 'soon',
      drive: 'Top up AdBlue if it is low. By law, many diesels count down to a speed limit or refuse to restart once the system '
        + 'has been faulty for a while, so do not leave it: the dashboard usually shows the distance left.',
      effects: ['AdBlue or emissions warning with a countdown', 'Speed limit or no restart when the countdown ends', 'Higher NOx emissions'],
      causes: [
        'Low or contaminated AdBlue',
        'Crystallised AdBlue blocking the injector or lines',
        'Frozen AdBlue with a failed heater in winter',
        'AdBlue pump, injector or quality sensor',
        ...(digits === '20EE' ? ['Ageing SCR catalyst or a NOx sensor'] : []),
      ],
      checks: ['Top up with fresh AdBlue from a sealed container', 'Have the injector checked for crystals'],
    };
  }
  if (inRange(n, 2200, 2214) || digits === '229E' || digits === '229F') {
    const bank = inRange(n, 2209, 2214) ? 2 : 1;
    const title = digits === '229F' ? 'NOx Sensor Circuit Range/Performance (Bank 1 Sensor 2)'
      : digits === '229E' ? 'NOx Sensor Circuit (Bank 1 Sensor 2)'
        : n === 2200 ? 'NOx Sensor Circuit (Bank 1)'
          : n === 2201 ? 'NOx Sensor Circuit Range/Performance (Bank 1)'
            : n === 2202 ? 'NOx Sensor Circuit Low Input (Bank 1)'
              : n === 2203 ? 'NOx Sensor Circuit High Input (Bank 1)'
                : undefined;
    const k: Knowledge = {
      summary: 'The NOx sensor measures nitrogen oxides in the exhaust, to control and check the NOx after-treatment. Its reading is missing or implausible.',
      severity: isDiesel(ctx) ? 'soon' : 'check',
      drive: 'Usually drivable, but on diesels with AdBlue this can start the countdown to a speed limit or no-restart. Have it checked soon.',
      effects: ['Emissions warning', 'Possible AdBlue countdown on diesels'],
      causes: ['NOx sensor (they have a limited life)', 'Its control unit or wiring', 'Exhaust leak'],
      checks: [CONNECTOR_CHECK],
      bank,
    };
    if (title) k.title = title;
    return k;
  }
  return undefined;
};

const swirl: Family = ({ n }) => {
  if (!inRange(n, 2004, 2019)) return undefined;
  const bank = n % 2 === 0 ? 1 : 2;
  const titles: Record<number, string> = {
    2004: 'Intake Manifold Runner Control Stuck Open (Bank 1)',
    2005: 'Intake Manifold Runner Control Stuck Open (Bank 2)',
    2006: 'Intake Manifold Runner Control Stuck Closed (Bank 1)',
    2007: 'Intake Manifold Runner Control Stuck Closed (Bank 2)',
    2008: 'Intake Manifold Runner Control Circuit/Open (Bank 1)',
  };
  const k: Knowledge = {
    summary: 'Flaps in the intake manifold (runner or swirl flaps) change how air enters the cylinders at different engine speeds. They are not moving as commanded.',
    severity: 'check',
    drive: 'Usually safe to drive. On some engines a broken flap can be drawn into the engine; if you hear rattling from the intake, have it checked promptly.',
    effects: ['Less power or torque at some engine speeds', 'Rough idle', 'Higher emissions'],
    causes: ['Carbon build-up jamming the flaps (common on diesels)', 'Actuator motor or vacuum diaphragm', 'Linkage broken or disconnected', 'Wiring or vacuum hose'],
    checks: ['Check the actuator linkage moves freely', 'Check the vacuum hose or connector to the actuator'],
    bank,
  };
  const title = titles[n];
  if (title) k.title = title;
  return k;
};

const otherP2: Family = ({ n }) => {
  const table: Record<number, Knowledge> = {
    2096: postCatTrim('Post Catalyst Fuel Trim System Too Lean (Bank 1)', 1),
    2097: postCatTrim('Post Catalyst Fuel Trim System Too Rich (Bank 1)', 1),
    2098: postCatTrim('Post Catalyst Fuel Trim System Too Lean (Bank 2)', 2),
    2099: postCatTrim('Post Catalyst Fuel Trim System Too Rich (Bank 2)', 2),
    2176: {
      title: 'Throttle Actuator Control System - Idle Position Not Learned',
      summary: 'The engine computer does not know where the throttle rests when closed.',
      severity: 'check',
      effects: ['High or unstable idle', 'Stalling'],
      causes: ['Throttle body cleaned or replaced without a relearn', 'Battery disconnected', 'Dirty throttle body'],
      checks: ['Run the throttle relearn procedure (some cars do it by leaving the ignition on for a set time; others need a tool)'],
    },
    2181: {
      title: 'Cooling System Performance',
      summary: 'The engine is not reaching or holding the expected temperature.',
      severity: 'soon',
      effects: ['Temperature too low or too high', 'Poor heating or overheating'],
      causes: ['Thermostat stuck open or closed', 'Low coolant', 'Cooling fan', 'Coolant temperature sensor'],
      checks: ['Check the coolant level with the engine cold', 'Watch Engine Coolant Temperature in the Signals tab during a drive'],
    },
    2279: {
      title: 'Intake Air System Leak',
      summary: 'Air is getting into the engine without being measured.',
      severity: 'soon',
      effects: ['Rough or high idle', 'Lean running and hesitation'],
      causes: ['Split intake hose or vacuum hose', 'Leaking intake manifold gasket', 'PCV valve or hose', 'Brake servo hose'],
      checks: ['Listen for hissing with the engine idling', 'A workshop smoke test finds leaks quickly'],
    },
    2610: {
      title: 'ECM/PCM Internal Engine Off Timer Performance',
      summary: 'The engine computer\'s clock that measures how long the engine has been off is not behaving as expected.',
      severity: 'check',
      effects: ['Some self-tests that depend on a cold start cannot run'],
      causes: ['Battery disconnected or weak', 'Engine computer software'],
      checks: ['Check the battery and its connections', 'Ask a dealer about software updates'],
    },
    2635: {
      title: 'Fuel Pump "A" Low Flow/Performance',
      summary: 'The fuel pump is not delivering enough fuel.',
      severity: 'soon',
      effects: ['Hesitation under load', 'Stalling or no start'],
      causes: ['Weak fuel pump', 'Clogged fuel filter', 'Wiring or connector with high resistance'],
      checks: ['Have the fuel pressure and flow measured'],
    },
  };
  return table[n];
};

function postCatTrim(title: string, bank: number): Knowledge {
  return {
    title,
    bank,
    summary: 'The oxygen sensor after the catalytic converter is seeing a mixture the engine computer cannot correct within its limits.',
    severity: 'check',
    effects: ['Emissions inspection fail', 'Usually little noticeable while driving'],
    causes: ['Exhaust leak near the downstream sensor', 'Ageing downstream oxygen sensor', 'Worn catalytic converter', 'Fuelling fault upstream'],
    checks: ['Check for exhaust leaks', 'Look for other fuel trim or oxygen sensor codes'],
  };
}

const cylinderDeactivation: Family = ({ digits }) => {
  if (!digits.startsWith('34')) return undefined;
  return {
    summary: 'Some engines shut down cylinders at light load to save fuel. That system has a fault.',
    severity: 'check',
    effects: ['Higher fuel consumption', 'Rough running if cylinders do not re-activate cleanly'],
    causes: ['Low or wrong engine oil (the system is oil-operated)', 'Deactivation solenoid or lifter', 'Wiring or connector'],
    checks: ['Check the oil level and grade', CONNECTOR_CHECK],
  };
};

const POWERTRAIN: Family[] = [
  vvt, crankCamCorrelation, o2Heater, turboControl, fuelPressure, ambientSensors, engineSensors, warmUp, o2Sensor, fuelTrim,
  injectors, engineProtection, boost, pumpMetering, misfire, ignitionSensors, egr, secondaryAir, catalyst, evap, fuelLevel,
  exhaustPressure, coolingFan, speedIdle, oilPressure, acRefrigerant, exhaustTemp, electrical, controlModule, transmission,
  hybrid, aftertreatment, swirl, otherP2, cylinderDeactivation,
];

// ...................................................................... U0xxx

/** Modules in the U01xx "lost communication" range whose numbers are standard. */
const MODULES: Record<string, { name: string; role: string; severity: DtcSeverity }> = {
  '00': { name: 'ECM/PCM "A"', role: 'the engine computer', severity: 'soon' },
  '01': { name: 'TCM', role: 'the transmission control module', severity: 'soon' },
  '02': { name: 'Transfer Case Control Module', role: 'four-wheel drive', severity: 'soon' },
  '03': { name: 'Gear Shift Module', role: 'the gear selector', severity: 'soon' },
  '04': { name: 'Cruise Control Module', role: 'cruise control', severity: 'check' },
  '05': { name: 'Fuel Injector Control Module', role: 'fuel injection', severity: 'soon' },
  '06': { name: 'Glow Plug Control Module', role: 'cold starting on diesels', severity: 'check' },
  '07': { name: 'Throttle Actuator Control Module', role: 'the throttle', severity: 'soon' },
  '09': { name: 'Fuel Pump Control Module', role: 'the fuel pump', severity: 'soon' },
  '10': { name: 'Drive Motor Control Module "A"', role: 'the electric drive motor', severity: 'soon' },
  '11': { name: 'Battery Energy Control Module "A"', role: 'the high-voltage battery', severity: 'soon' },
  '21': { name: 'Anti-Lock Brake System (ABS) Control Module', role: 'anti-lock brakes and often stability control', severity: 'soon' },
  '22': { name: 'Vehicle Dynamics Control Module', role: 'stability control', severity: 'soon' },
  '26': { name: 'Steering Angle Sensor Module', role: 'steering angle for stability control', severity: 'soon' },
  '27': { name: 'Tire Pressure Monitor Module', role: 'tyre pressure warnings', severity: 'check' },
  '28': { name: 'Park Brake Control Module', role: 'the electric parking brake', severity: 'soon' },
  '29': { name: 'Brake System Control Module', role: 'the brake system', severity: 'soon' },
  '31': { name: 'Power Steering Control Module', role: 'power steering', severity: 'soon' },
  '40': { name: 'Body Control Module', role: 'lights, locks, wipers and other body functions', severity: 'check' },
  '51': { name: 'Restraints Control Module', role: 'the airbags', severity: 'soon' },
  '55': { name: 'Instrument Panel Cluster (IPC) Control Module', role: 'the instrument cluster', severity: 'check' },
  '64': { name: 'HVAC Control Module', role: 'heating and air conditioning', severity: 'info' },
  '84': { name: 'Radio', role: 'the audio system', severity: 'info' },
};

const BATTERY_TIP = 'Network codes often appear after a flat battery, jump start or battery change, and mean nothing on their own. '
  + 'Clear them and see whether they return.';

const lostCommunication: Family = ({ digits }) => {
  // U0100-U02FF are all "lost communication"; only the U01 names are listed here.
  const lost = /^0[12]([0-9A-F]{2})$/.exec(digits);
  const invalid = /^04(01|02|15)$/.exec(digits);
  if (!lost && !invalid) return undefined;
  const key = lost
    ? (digits[1] === '1' ? lost[1] : undefined)
    : ({ '01': '00', '02': '01', '15': '21' } as Record<string, string>)[invalid?.[1] ?? ''];
  const m = key === undefined ? undefined : MODULES[key];
  const moduleName = m?.name ?? 'another control module (entry ' + digits.slice(1) + ' in the standard list)';
  const severity: DtcSeverity = m?.severity ?? 'check';
  const k: Knowledge = {
    summary: (lost
      ? 'The module that reported this stopped hearing from ' + moduleName + ' on the car\'s data network.'
      : 'The module that reported this received data from ' + moduleName + ' that did not make sense.')
      + (m ? ' That module handles ' + m.role + ', which may not be working while the fault is present.' : ''),
    severity,
    drive: m?.role.includes('brake') || m?.role.includes('stability')
      ? 'The brakes still work, but anti-lock and stability control may be off: brake gently and allow extra distance. Have it checked soon.'
      : m?.role.includes('airbag')
        ? 'The car drives normally, but the airbags may not deploy in a crash. Have it checked soon.'
        : m?.role.includes('steering')
          ? 'Steering may become heavy. Drive carefully and have it checked soon.'
          : DEFAULT_DRIVE[severity],
    effects: ['Warning lamps for the functions that module provides', 'Those functions may be unavailable'],
    causes: [
      'Low battery voltage, especially during starting',
      'That module\'s fuse, power supply or ground',
      'Damaged or corroded network wiring or connector',
      'The module itself',
    ],
    checks: [BATTERY_TIP, 'Check the fuses for that module', 'Have the modules on the network scanned with a tool that can talk to all of them'],
  };
  if (m) {
    k.module = m.name;
    k.title = (lost ? 'Lost Communication With ' : 'Invalid Data Received From ') + m.name;
  }
  return k;
};

const networkBus: Family = ({ digits }) => {
  const titles: Record<string, string> = {
    '0001': 'High Speed CAN Communication Bus',
    '0010': 'Medium Speed CAN Communication Bus',
    '0019': 'Low Speed CAN Communication Bus',
    '0028': 'Vehicle Communication Bus "A"',
    '0073': 'Control Module Communication Bus "A" Off',
    '0300': 'Internal Control Module Software Incompatibility',
  };
  if (!digits.startsWith('00') && digits !== '0300') return undefined;
  const software = digits === '0300';
  const k: Knowledge = {
    summary: software
      ? 'Two modules have software versions that do not work together, typically after one was replaced or reprogrammed.'
      : 'The car\'s data network itself has a fault, so modules cannot talk to each other reliably.',
    severity: 'soon',
    effects: ['Many warning lamps at once', 'Functions dropping in and out', 'Possible no start'],
    causes: software
      ? ['A module replaced or updated without matching the others', 'Interrupted programming']
      : ['Low battery voltage', 'Damaged or corroded network wiring', 'A faulty module pulling the network down', 'Water in a connector'],
    checks: software ? ['Have a dealer check module software versions'] : [BATTERY_TIP, 'A workshop can measure the network wiring'],
  };
  const title = titles[digits];
  if (title) k.title = title;
  return k;
};

const NETWORK: Family[] = [lostCommunication, networkBus];

// ...................................................................... B, C

const restraints: Family = ({ digits }) => {
  if (!digits.startsWith('00')) return undefined;
  return {
    summary: 'A fault in the airbag and seat-belt pretensioner system.',
    severity: 'soon',
    drive: 'The car drives normally, but airbags or pretensioners may not work in a crash. Have it checked soon, and do not '
      + 'work on airbag parts yourself: they can deploy.',
    effects: ['Airbag warning lamp on'],
    causes: ['Connector under a seat disturbed', 'Clock spring in the steering wheel', 'Seat occupancy sensor', 'Airbag or pretensioner circuit'],
    checks: ['Have the airbag module read with a tool that talks to it: generic OBD-II usually cannot'],
  };
};

const BODY: Family[] = [restraints];
const CHASSIS: Family[] = [];

// ------------------------------------------------------------------ fallback

function fallback(p: Parsed, s: DtcStructure): Knowledge {
  if (s.definedBy === 'manufacturer') {
    const maker = p.ctx.manufacturer;
    return {
      summary: 'This code is defined by ' + (maker ?? 'the carmaker') + ', not by the OBD-II standard, so its exact meaning is '
        + 'in ' + (maker ? maker + '\'s' : 'the carmaker\'s') + ' own documentation. The same number can mean different things on '
        + 'different makes. ' + (p.letter === 'P' ? 'Its area, going by the usual grouping: ' + s.area.replace(/ \(usually\)$/, '').toLowerCase() + '.' : 'System: ' + s.system.toLowerCase() + '.'),
      severity: 'check',
      drive: 'Judge by how the car behaves: if it drives normally and no red warning lamp is on, it is usually fine to continue '
        + 'carefully. Have it looked up and checked.',
      effects: ['Depends on the carmaker\'s definition'],
      causes: ['See the carmaker\'s definition of this code'],
      checks: [
        'Look up "' + (maker ? maker + ' ' : '') + p.code + '": owner forums and workshop manuals for the make usually have it',
        'A workshop with a scan tool for the make can read its full description and freeze frame',
      ],
    };
  }
  const area = s.area;
  if (s.system === 'Chassis') {
    return {
      summary: 'A fault in the brakes, steering, suspension, or traction and stability control.',
      severity: 'soon',
      drive: 'Drive carefully: anti-lock braking, stability control or power steering may be affected. Have it checked soon.',
      effects: ['ABS, stability or steering warning lamps', 'Those systems may be off'],
      causes: ['Wheel speed sensor or its wiring (a common one)', 'Low brake fluid', 'Module or wiring fault'],
      checks: ['Check the brake fluid level', 'Have the brake and chassis modules read with a tool that talks to them directly'],
    };
  }
  if (s.system === 'Body') {
    return {
      summary: 'A fault in a body system: comfort, lighting, locking, climate control or similar.',
      severity: 'check',
      effects: ['Depends on the system: usually a comfort or convenience function'],
      causes: ['A switch, motor, sensor or its wiring', 'The module for that function'],
      checks: ['Have the body modules read with a tool that talks to them directly'],
    };
  }
  if (s.system === 'Network') {
    return {
      summary: 'A fault in how the car\'s control modules talk to each other.',
      severity: 'check',
      effects: ['Warning lamps for affected systems', 'Functions dropping out'],
      causes: ['Low battery voltage', 'Network wiring or connector', 'A module fault'],
      checks: [BATTERY_TIP],
    };
  }
  if (/Transmission/.test(area)) {
    const { title: _unused, ...rest } = transmissionKnowledge(Number.NaN);
    return rest;
  }
  if (/Ignition/.test(area)) return generic(area, 'soon');
  if (/Control module/.test(area)) return generic(area, 'soon');
  return generic(area, 'check');
}

function generic(area: string, severity: DtcSeverity): Knowledge {
  return {
    summary: 'A generic OBD-II fault in this area: ' + area.toLowerCase() + '. The exact wording is not on file in this app, '
      + 'but the code means the same on every car.',
    severity,
    effects: ['Depends on the part: often rougher running, higher consumption or an emissions inspection fail'],
    causes: ['A sensor or actuator in that area', 'Its wiring or connector'],
    checks: [
      'Look up the code: generic codes have the same definition for every make',
      CONNECTOR_CHECK,
      SIGNALS_TIP,
    ],
  };
}

// -------------------------------------------------------------------- titles

/**
 * Names for generic codes. Families above generate the names of the groups
 * they cover and take precedence; these fill in the rest.
 */
const TITLES: Record<string, string> = {
  P0215: 'Engine Shutoff Solenoid',
  P0216: 'Injector/Injection Timing Control Circuit',
  P0230: 'Fuel Pump Primary Circuit',
  P0231: 'Fuel Pump Secondary Circuit Low',
  P0232: 'Fuel Pump Secondary Circuit High',
  P042E: 'Exhaust Gas Recirculation "A" Control Stuck Open',
  P0313: 'Misfire Detected with Low Fuel',

  // --- fuel and air metering -------------------------------------------------
  P0100: 'Mass or Volume Air Flow Circuit Malfunction',
  P0101: 'Mass or Volume Air Flow Circuit Range/Performance Problem',
  P0102: 'Mass or Volume Air Flow Circuit Low Input',
  P0103: 'Mass or Volume Air Flow Circuit High Input',
  P0105: 'Manifold Absolute Pressure/Barometric Pressure Circuit Malfunction',
  P0106: 'Manifold Absolute Pressure/Barometric Pressure Range/Performance',
  P0107: 'Manifold Absolute Pressure/Barometric Pressure Circuit Low Input',
  P0108: 'Manifold Absolute Pressure/Barometric Pressure Circuit High Input',
  P0110: 'Intake Air Temperature Circuit Malfunction',
  P0111: 'Intake Air Temperature Circuit Range/Performance Problem',
  P0112: 'Intake Air Temperature Circuit Low Input',
  P0113: 'Intake Air Temperature Circuit High Input',
  P0115: 'Engine Coolant Temperature Circuit Malfunction',
  P0116: 'Engine Coolant Temperature Circuit Range/Performance Problem',
  P0117: 'Engine Coolant Temperature Circuit Low Input',
  P0118: 'Engine Coolant Temperature Circuit High Input',
  P0120: 'Throttle/Pedal Position Sensor A Circuit Malfunction',
  P0121: 'Throttle/Pedal Position Sensor A Circuit Range/Performance',
  P0122: 'Throttle/Pedal Position Sensor A Circuit Low Input',
  P0123: 'Throttle/Pedal Position Sensor A Circuit High Input',
  P0125: 'Insufficient Coolant Temperature for Closed Loop Fuel Control',
  P0128: 'Coolant Thermostat Below Regulating Temperature',
  P0130: 'O2 Sensor Circuit Malfunction (Bank 1 Sensor 1)',
  P0131: 'O2 Sensor Circuit Low Voltage (Bank 1 Sensor 1)',
  P0132: 'O2 Sensor Circuit High Voltage (Bank 1 Sensor 1)',
  P0133: 'O2 Sensor Circuit Slow Response (Bank 1 Sensor 1)',
  P0134: 'O2 Sensor Circuit No Activity Detected (Bank 1 Sensor 1)',
  P0135: 'O2 Sensor Heater Circuit Malfunction (Bank 1 Sensor 1)',
  P0136: 'O2 Sensor Circuit Malfunction (Bank 1 Sensor 2)',
  P0137: 'O2 Sensor Circuit Low Voltage (Bank 1 Sensor 2)',
  P0138: 'O2 Sensor Circuit High Voltage (Bank 1 Sensor 2)',
  P0139: 'O2 Sensor Circuit Slow Response (Bank 1 Sensor 2)',
  P0140: 'O2 Sensor Circuit No Activity Detected (Bank 1 Sensor 2)',
  P0141: 'O2 Sensor Heater Circuit Malfunction (Bank 1 Sensor 2)',
  P0150: 'O2 Sensor Circuit Malfunction (Bank 2 Sensor 1)',
  P0151: 'O2 Sensor Circuit Low Voltage (Bank 2 Sensor 1)',
  P0152: 'O2 Sensor Circuit High Voltage (Bank 2 Sensor 1)',
  P0155: 'O2 Sensor Heater Circuit Malfunction (Bank 2 Sensor 1)',
  P0171: 'System Too Lean (Bank 1)',
  P0172: 'System Too Rich (Bank 1)',
  P0174: 'System Too Lean (Bank 2)',
  P0175: 'System Too Rich (Bank 2)',
  P0180: 'Fuel Temperature Sensor A Circuit Malfunction',
  P0190: 'Fuel Rail Pressure Sensor Circuit Malfunction',
  P0191: 'Fuel Rail Pressure Sensor Circuit Range/Performance',
  P0192: 'Fuel Rail Pressure Sensor Circuit Low Input',
  P0193: 'Fuel Rail Pressure Sensor Circuit High Input',

  // --- injectors -------------------------------------------------------------
  P0201: 'Injector Circuit Malfunction - Cylinder 1',
  P0202: 'Injector Circuit Malfunction - Cylinder 2',
  P0203: 'Injector Circuit Malfunction - Cylinder 3',
  P0204: 'Injector Circuit Malfunction - Cylinder 4',
  P0205: 'Injector Circuit Malfunction - Cylinder 5',
  P0206: 'Injector Circuit Malfunction - Cylinder 6',
  P0217: 'Engine Over Temperature Condition',
  P0219: 'Engine Overspeed Condition',
  P0234: 'Engine Overboost Condition',
  P0235: 'Turbocharger Boost Sensor A Circuit Malfunction',
  P0236: 'Turbocharger Boost Sensor A Circuit Range/Performance',
  P0237: 'Turbocharger Boost Sensor A Circuit Low',
  P0238: 'Turbocharger Boost Sensor A Circuit High',
  P0243: 'Turbocharger Wastegate Solenoid A Malfunction',
  P0245: 'Turbocharger Wastegate Solenoid A Low',
  P0246: 'Turbocharger Wastegate Solenoid A High',
  P0251: 'Injection Pump Fuel Metering Control A Malfunction',
  P0263: 'Cylinder 1 Contribution/Balance Fault',
  P0299: 'Turbocharger/Supercharger Underboost',

  // --- ignition and misfire --------------------------------------------------
  P0300: 'Random/Multiple Cylinder Misfire Detected',
  P0301: 'Cylinder 1 Misfire Detected',
  P0302: 'Cylinder 2 Misfire Detected',
  P0303: 'Cylinder 3 Misfire Detected',
  P0304: 'Cylinder 4 Misfire Detected',
  P0305: 'Cylinder 5 Misfire Detected',
  P0306: 'Cylinder 6 Misfire Detected',
  P0307: 'Cylinder 7 Misfire Detected',
  P0308: 'Cylinder 8 Misfire Detected',
  P0315: 'Crankshaft Position System Variation Not Learned',
  P0320: 'Ignition/Distributor Engine Speed Input Circuit Malfunction',
  P0325: 'Knock Sensor 1 Circuit Malfunction (Bank 1)',
  P0327: 'Knock Sensor 1 Circuit Low Input (Bank 1)',
  P0328: 'Knock Sensor 1 Circuit High Input (Bank 1)',
  P0335: 'Crankshaft Position Sensor A Circuit Malfunction',
  P0336: 'Crankshaft Position Sensor A Circuit Range/Performance',
  P0340: 'Camshaft Position Sensor Circuit Malfunction',
  P0341: 'Camshaft Position Sensor Circuit Range/Performance',
  P0344: 'Camshaft Position Sensor Circuit Intermittent',

  // --- emission controls -----------------------------------------------------
  P0401: 'Exhaust Gas Recirculation Flow Insufficient Detected',
  P0402: 'Exhaust Gas Recirculation Flow Excessive Detected',
  P0403: 'Exhaust Gas Recirculation Circuit Malfunction',
  P0404: 'Exhaust Gas Recirculation Circuit Range/Performance',
  P0405: 'Exhaust Gas Recirculation Sensor A Circuit Low',
  P0406: 'Exhaust Gas Recirculation Sensor A Circuit High',
  P0410: 'Secondary Air Injection System Malfunction',
  P0420: 'Catalyst System Efficiency Below Threshold (Bank 1)',
  P0421: 'Warm Up Catalyst Efficiency Below Threshold (Bank 1)',
  P0430: 'Catalyst System Efficiency Below Threshold (Bank 2)',
  P0440: 'Evaporative Emission Control System Malfunction',
  P0441: 'Evaporative Emission Control System Incorrect Purge Flow',
  P0442: 'Evaporative Emission Control System Leak Detected (small leak)',
  P0446: 'Evaporative Emission Control System Vent Control Circuit Malfunction',
  P0455: 'Evaporative Emission Control System Leak Detected (large leak)',
  P0456: 'Evaporative Emission Control System Leak Detected (very small leak)',
  P0480: 'Cooling Fan 1 Control Circuit Malfunction',

  // --- speed, idle, ECU ------------------------------------------------------
  P0500: 'Vehicle Speed Sensor Malfunction',
  P0501: 'Vehicle Speed Sensor Range/Performance',
  P0505: 'Idle Control System Malfunction',
  P0506: 'Idle Control System RPM Lower Than Expected',
  P0507: 'Idle Control System RPM Higher Than Expected',
  P0521: 'Engine Oil Pressure Sensor Range/Performance',
  P0524: 'Engine Oil Pressure Too Low',
  P0530: 'A/C Refrigerant Pressure Sensor Circuit Malfunction',
  P0562: 'System Voltage Low',
  P0563: 'System Voltage High',
  P0571: 'Brake Switch A Circuit Malfunction',
  P0600: 'Serial Communication Link Malfunction',
  P0601: 'Internal Control Module Memory Check Sum Error',
  P0602: 'Control Module Programming Error',
  P0606: 'ECM/PCM Processor Fault',
  P0620: 'Generator Control Circuit Malfunction',
  P0627: 'Fuel Pump A Control Circuit Open',
  P0645: 'A/C Clutch Relay Control Circuit',

  // --- transmission ----------------------------------------------------------
  P0700: 'Transmission Control System Malfunction',
  P0701: 'Transmission Control System Range/Performance',
  P0705: 'Transmission Range Sensor Circuit Malfunction',
  P0710: 'Transmission Fluid Temperature Sensor Circuit Malfunction',
  P0715: 'Input/Turbine Speed Sensor Circuit Malfunction',
  P0720: 'Output Speed Sensor Circuit Malfunction',
  P0730: 'Incorrect Gear Ratio',
  P0731: 'Gear 1 Incorrect Ratio',
  P0732: 'Gear 2 Incorrect Ratio',
  P0733: 'Gear 3 Incorrect Ratio',
  P0734: 'Gear 4 Incorrect Ratio',
  P0740: 'Torque Converter Clutch Circuit Malfunction',
  P0741: 'Torque Converter Clutch Circuit Performance or Stuck Off',
  P0750: 'Shift Solenoid A Malfunction',
  P0755: 'Shift Solenoid B Malfunction',

  // --- diesel aftertreatment -------------------------------------------------
  P0470: 'Exhaust Pressure Sensor Malfunction',
  P0471: 'Exhaust Pressure Sensor Range/Performance',
  P2002: 'Diesel Particulate Filter Efficiency Below Threshold (Bank 1)',
  P2003: 'Diesel Particulate Filter Efficiency Below Threshold (Bank 2)',
  P2031: 'Exhaust Gas Temperature Sensor Circuit (Bank 1 Sensor 2)',
  P2032: 'Exhaust Gas Temperature Sensor Circuit Low (Bank 1 Sensor 2)',
  P2033: 'Exhaust Gas Temperature Sensor Circuit High (Bank 1 Sensor 2)',
  P2080: 'Exhaust Gas Temperature Sensor Circuit Range/Performance (Bank 1 Sensor 1)',
  P2084: 'Exhaust Gas Temperature Sensor Circuit Range/Performance (Bank 1 Sensor 2)',
  P2452: 'Diesel Particulate Filter Pressure Sensor A Circuit',
  P2453: 'Diesel Particulate Filter Pressure Sensor A Circuit Range/Performance',
  P2454: 'Diesel Particulate Filter Pressure Sensor A Circuit Low',
  P2455: 'Diesel Particulate Filter Pressure Sensor A Circuit High',
  P2458: 'Diesel Particulate Filter Regeneration Duration',
  P2459: 'Diesel Particulate Filter Regeneration Frequency',
  P2463: 'Diesel Particulate Filter Restriction - Soot Accumulation',
  P204F: 'Reductant System Performance',
  P20E8: 'Reductant Pressure Too Low',
  P2200: 'NOx Sensor Circuit (Bank 1)',
  P2201: 'NOx Sensor Circuit Range/Performance (Bank 1)',
  P2202: 'NOx Sensor Circuit Low Input (Bank 1)',
  P2203: 'NOx Sensor Circuit High Input (Bank 1)',
  P229F: 'NOx Sensor Circuit Range/Performance (Bank 1 Sensor 2)',

  // --- common P2xxx ----------------------------------------------------------
  P2004: 'Intake Manifold Runner Control Stuck Open (Bank 1)',
  P2006: 'Intake Manifold Runner Control Stuck Closed (Bank 1)',
  P2008: 'Intake Manifold Runner Control Circuit Open (Bank 1)',
  P2096: 'Post Catalyst Fuel Trim System Too Lean (Bank 1)',
  P2097: 'Post Catalyst Fuel Trim System Too Rich (Bank 1)',
  P2100: 'Throttle Actuator Control Motor Circuit Open',
  P2101: 'Throttle Actuator Control Motor Circuit Range/Performance',
  P2119: 'Throttle Actuator Control Throttle Body Range/Performance',
  P2122: 'Throttle/Pedal Position Sensor D Circuit Low Input',
  P2135: 'Throttle/Pedal Position Sensor A/B Voltage Correlation',
  P2138: 'Throttle/Pedal Position Sensor D/E Voltage Correlation',
  P2187: 'System Too Lean at Idle (Bank 1)',
  P2188: 'System Too Rich at Idle (Bank 1)',
  P2195: 'O2 Sensor Signal Stuck Lean (Bank 1 Sensor 1)',
  P2196: 'O2 Sensor Signal Stuck Rich (Bank 1 Sensor 1)',
  P2270: 'O2 Sensor Signal Stuck Lean (Bank 1 Sensor 2)',
  P2279: 'Intake Air System Leak',
  P2413: 'Exhaust Gas Recirculation System Performance',

  // --- network ---------------------------------------------------------------
  U0001: 'High Speed CAN Communication Bus',
  U0100: 'Lost Communication With ECM/PCM A',
  U0101: 'Lost Communication With TCM',
  U0121: 'Lost Communication With ABS Control Module',
  U0140: 'Lost Communication With Body Control Module',
  U0155: 'Lost Communication With Instrument Panel Cluster',
  U0401: 'Invalid Data Received From ECM/PCM A',
};
